import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BookOpen } from "lucide-react";
import { api } from "../../lib/api";
import RecoveryBanner from "./components/RecoveryBanner";
import AgentGlossaryModal from "./components/AgentGlossaryModal";
import CancellationPanel from "./components/CancellationPanel";
import DirectionCancelPanel, { isDirectionCancelTicket } from "./components/DirectionCancelPanel";
import WholeTripFlightSection from "./components/WholeTripFlightSection";
import NextActionCard, { StatusChip } from "./components/NextActionCard";
import FoldSection from "./components/FoldSection";
import WholeTripSendCard from "./components/WholeTripSendCard";
import ReschedulingPanel from "./components/ReschedulingPanel";
import RescheduleNextActionCard, { RescheduleStatusChip } from "./components/RescheduleNextActionCard";
import StatusBadge from "./components/StatusBadge";
import QueryTypeBadge from "./components/QueryTypeBadge";
import BookingsTabTable from "./components/BookingsTabTable";
import TicketHistoryTimeline from "./components/TicketHistoryTimeline";
import CustomerHistoryCard from "./components/CustomerHistoryCard";
import { formatVal, formatDate, formatINR, formatISOTimestampsInText, formatHistoryTimestamp } from "./utils/formatters";
import { normalizeTicketDetail } from "./utils/normalizer";
import {
  findCancellationRequestText,
  parseCancellationRequestDetails,
  findReschedulingRequestText,
  parseReschedulingRequestDetails,
  findRefundRequestText,
  parseRefundRequestDetails,
} from "./utils/parsers";
import {
  CANCELLATION_TYPE_SELECT,
  REFUND_STATUS_LABELS,
  TICKET_TOP_FIELDS,
  DEFAULT_CANCELLATION_TYPE_LABEL,
} from "./constants";
import {
  getSearchPayloadRows,
  getActualPayloadRows,
  getFareInfoRows,
  getFareDetailRows,
} from "../../utils/bookingDetailParsers";

/** Normalize a /admin/tickets/bookings response (raw array or {past,current,upcoming}-shaped
 *  object) into {past, current, upcoming} buckets by travel date, relative to today. */
function bucketBookings(rawBookings) {
  if (Array.isArray(rawBookings)) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const past = [];
    const current = [];
    const upcoming = [];
    rawBookings.forEach((b) => {
      const d = b.travel_date ?? b.travel_date_from ?? b.dep_date_time;
      if (!d) {
        current.push(b);
        return;
      }
      const t = new Date(d);
      t.setHours(0, 0, 0, 0);
      if (t < now) past.push(b);
      else if (t > now) upcoming.push(b);
      else current.push(b);
    });
    return { past, current, upcoming };
  } else if (rawBookings && typeof rawBookings === "object") {
    return {
      past: rawBookings.past_bookings ?? rawBookings.past ?? [],
      current: rawBookings.current_bookings ?? rawBookings.current ?? [],
      upcoming: rawBookings.upcoming_bookings ?? rawBookings.upcoming ?? [],
    };
  }
  return { past: [], current: [], upcoming: [] };
}

/** search_payload rows come back as one flat list with "— Section —" header rows
 *  (see getSearchPayloadRows). Slice out just the rows under one header so Flight/
 *  Segments and Passengers can render as their own sections on the ticket page. */
const SEARCH_PAYLOAD_SECTION_LABELS = ["— Top level —", "— Fare details —", "— Segments —", "— Passengers —"];
function sliceRowsSection(rows, sectionLabel) {
  const startIdx = rows.findIndex((r) => r.label === sectionLabel);
  if (startIdx === -1) return [];
  let endIdx = rows.length;
  for (let i = startIdx + 1; i < rows.length; i++) {
    if (SEARCH_PAYLOAD_SECTION_LABELS.includes(rows[i].label)) {
      endIdx = i;
      break;
    }
  }
  return rows.slice(startIdx + 1, endIdx);
}

function BookingRowsTable({ rows, emptyMessage = "No data available." }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="px-4 py-6 text-sm text-gray-500 text-center">{emptyMessage}</div>
    );
  }
  return (
    <table className="w-full text-sm table-fixed">
      <colgroup>
        <col className="w-[45%] min-w-[140px]" />
        <col className="w-[55%] min-w-[180px]" />
      </colgroup>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={idx} className={r.isSection ? "bg-gray-100" : ""}>
            <td className={`py-2 px-4 align-top ${r.isSection ? "font-bold text-gray-800" : "text-gray-600"}`}>
              {r.label}
            </td>
            <td className="py-2 px-4 text-right text-gray-800 break-words align-top">
              {r.value ?? "-"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Raw booking-payload viewer for the ticket page, mirroring the modal
 *  BookingViewEditPage.jsx uses, built on the same shared row parsers. */
function RawBookingPayloadModal({ open, onClose, type, booking }) {
  if (!open) return null;
  const title =
    type === "search" ? "User Booking Data" : type === "actual" ? "Booking Passenger Details" : "Search Fare Detail";
  const rows =
    type === "search" ? getSearchPayloadRows(booking?.search_payload)
    : type === "actual" ? getActualPayloadRows(booking?.actual_payload)
    : getFareDetailRows(booking?.fare_detail);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:bg-gray-100"
          >
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto flex-1 min-h-0 w-full">
          <BookingRowsTable rows={rows} emptyMessage="No data or invalid JSON" />
        </div>
      </div>
    </div>
  );
}

export default function TicketViewPage() {
  const { ticket_id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [bookings, setBookings] = useState({ past: [], current: [], upcoming: [] });
  const [passengers, setPassengers] = useState([]);
  const [passengersLoading, setPassengersLoading] = useState(false);
  const [selectedPassengers, setSelectedPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [duplicateTickets, setDuplicateTickets] = useState([]);

  const [bookingTab, setBookingTab] = useState("upcoming");

  const [statusValue, setStatusValue] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [closingComment, setClosingComment] = useState("");
  const [pendingCloseStatus, setPendingCloseStatus] = useState("");
  const [tboVerified, setTboVerified] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundSaving, setRefundSaving] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  // Whole Trip: the second flight's ticket, shown inside this one.
  const [companionTicket, setCompanionTicket] = useState(null);
  const [companionBooking, setCompanionBooking] = useState(null);
  // Bumped after the shared Step 3 sends, so Flight 2 reloads its own record.
  const [companionReload, setCompanionReload] = useState(0);
  const handleCompanionLoaded = useCallback((t, b) => {
    setCompanionTicket(t);
    setCompanionBooking(b ?? null);
  }, []);

  const [cancelType, setCancelType] = useState(1);
  const [cancelTypeLabel, setCancelTypeLabel] = useState(
    DEFAULT_CANCELLATION_TYPE_LABEL
  );

  const [recoveryRefreshLoading, setRecoveryRefreshLoading] = useState(false);

  const [bookingFailedPnr, setBookingFailedPnr] = useState("");
  const [bookingFailedTboBookingId, setBookingFailedTboBookingId] = useState("");
  const [bookingFailedPnrLoading, setBookingFailedPnrLoading] = useState(false);
  /** Inline (non-toast) result of the last confirm-booking-pnr attempt: null, or
   *  { type: "lookup_failed" | "not_ticketed" | "in_progress", message, checkedLine? } */
  const [bookingFailedResult, setBookingFailedResult] = useState(null);
  const [instantRefundLoading, setInstantRefundLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);

  // ── Full booking detail for tickets with a linked booking (ticket.booking_id) ───
  const [linkedBookingDetail, setLinkedBookingDetail] = useState(null);
  const [linkedBookingDetailLoading, setLinkedBookingDetailLoading] = useState(false);
  const [linkedBookingDetailError, setLinkedBookingDetailError] = useState("");
  const [rawPayloadModal, setRawPayloadModal] = useState({ open: false, type: null });

  // ── Failed-ticket booking edit (matches Bookings Dashboard pattern) ───────
  const [editBookingOpen, setEditBookingOpen] = useState(false);
  const [editBookingConfirmOpen, setEditBookingConfirmOpen] = useState(false);
  const [editBookingFlightId, setEditBookingFlightId] = useState("");
  const [editBookingPnr, setEditBookingPnr] = useState("");
  const [editBookingAirlinePnr, setEditBookingAirlinePnr] = useState("");
  const [editBookingErrors, setEditBookingErrors] = useState({ flightId: "", pnr: "" });
  const [editBookingSaving, setEditBookingSaving] = useState(false);
  /** Inline (non-toast) result of a 422 NAME_MISMATCH from the PNR save:
   *  null, or { mismatches: [{first_name,last_name}], tboPassengers: [{first_name,last_name}] } */
  const [editBookingNameMismatch, setEditBookingNameMismatch] = useState(null);

  const [refundChargeForm, setRefundChargeForm] = useState({
    cancellation_charge: "",
    refund_amount: "",      // cash to original payment method
    convenience_fee: "",
    total_amount_paid: "",
    promo_code_amount: "",  // forfeited on cancellation
    navcash_amount: "",     // reversed back to NavCash wallet
    giftcard_amount: "",    // reversed back to gift card
  });
  const [refundChargeSubmitting, setRefundChargeSubmitting] = useState(false);
  const [refundChargeSubmitted, setRefundChargeSubmitted] = useState(false);
  const [refundTboCharges, setRefundTboCharges] = useState(null);
  const [refundTboLoading, setRefundTboLoading] = useState(false);
  const [refundExecSubmitting, setRefundExecSubmitting] = useState(false);
  const [refundExecRemarks, setRefundExecRemarks] = useState("");
  const [refundExecDone, setRefundExecDone] = useState(false);
  const [refundExecResult, setRefundExecResult] = useState(null);

  const cancellationRequestDetails = useMemo(() => {
    if (!ticket) return null;
    if ((ticket.query_type || "").toUpperCase() !== "CANCELLATION") return null;
    const blob = findCancellationRequestText(ticket);
    if (blob) return parseCancellationRequestDetails(blob);

    // Fallback for bot-created cancellation tickets
    // without [FULL CANCELLATION] marker
    if (
      ticket.action_required &&
      ticket.action_required.includes("Bot cancellation")
    ) {
      return {
        cancelType: "full",
        passengersLine: "",
        passengerNames: [],
        customerDesc: ticket.action_required,
      };
    }
    return null;
  }, [ticket]);

  const ticketHasNoPnr = useMemo(
    () => !ticket?.pnr || String(ticket.pnr).trim() === "",
    [ticket?.pnr]
  );

  const searchPayloadRows = useMemo(
    () => (linkedBookingDetail ? getSearchPayloadRows(linkedBookingDetail.search_payload) : []),
    [linkedBookingDetail]
  );
  const segmentsDetailRows = useMemo(
    () => sliceRowsSection(searchPayloadRows, "— Segments —"),
    [searchPayloadRows]
  );
  const passengersDetailRows = useMemo(
    () => sliceRowsSection(searchPayloadRows, "— Passengers —"),
    [searchPayloadRows]
  );
  const fareInfoDetailRows = useMemo(
    () => (linkedBookingDetail ? getFareInfoRows(linkedBookingDetail.fare_info) : []),
    [linkedBookingDetail]
  );
  const fareInfoDetailTotal = useMemo(
    () => fareInfoDetailRows.reduce((sum, r) => sum + r.value, 0),
    [fareInfoDetailRows]
  );
  const fareInfoDetailHasTotal = useMemo(
    () => fareInfoDetailRows.some((r) => String(r.label).toLowerCase() === "total"),
    [fareInfoDetailRows]
  );
  /** The trimmed booking record already loaded via /admin/tickets/bookings, matched
   *  by booking_id — used for Payment/Invoice fields the full-detail endpoint omits. */
  const linkedBookingSummary = useMemo(() => {
    if (!ticket?.booking_id) return null;
    const all = [
      ...(bookings?.upcoming || []),
      ...(bookings?.current || []),
      ...(bookings?.past || []),
    ];
    return all.find((b) => Number(b.booking_id) === Number(ticket.booking_id)) || all[0] || null;
  }, [ticket?.booking_id, bookings]);

  const reschedulingRequestDetails = useMemo(() => {
    if (!ticket) return null;
    if ((ticket.query_type || "").toUpperCase() !== "RESCHEDULING") return null;

    // Original booking for airline fallback and fare info
    const originalBooking = [
      ...(bookings?.upcoming || []),
      ...(bookings?.current  || []),
      ...(bookings?.past     || []),
    ][0] ?? null;

    // Original booking airline shown as "preferred airline" (what the customer had before)
    const originalAirline = originalBooking
      ? [originalBooking.airline_code, originalBooking.airline_flight_no].filter(Boolean).join(" ") || null
      : null;

    const blob = findReschedulingRequestText(ticket);
    if (blob) {
      const parsed = parseReschedulingRequestDetails(blob);
      return {
        ...parsed,
        preferredAirline: originalAirline || parsed.preferredAirline || "",
      };
    }

    // Fallback: bot-created tickets — derive display fields from reschedule_details_parsed
    if (ticket.action_required || ticket.reschedule_details_parsed) {
      const d = ticket.reschedule_details_parsed;

      const rawDate = d?.preferred_date || d?.new_departure || null;
      let preferredDate = "";
      if (rawDate) {
        try {
          const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(rawDate).trim());
          const dt = isDateOnly ? new Date(`${rawDate}T00:00:00`) : new Date(rawDate);
          if (!isNaN(dt.getTime())) {
            preferredDate = dt.toLocaleDateString("en-IN", {
              day: "numeric", month: "short", year: "numeric",
            });
          } else {
            preferredDate = String(rawDate);
          }
        } catch {
          preferredDate = String(rawDate);
        }
      }

      return {
        reason: d?.reschedule_reason || "Submitted via bot",
        preferredDate,
        preferredAirline: originalAirline || "",
        passengerNames: [],
        customerDesc: ticket.action_required || "",
      };
    }
    return null;
  }, [ticket, bookings]);

  const refundRequestDetails = useMemo(() => {
    if (!ticket) return null;
    if ((ticket.query_type || "").toUpperCase() !== "REFUND") return null;
    const blob = findRefundRequestText(ticket);
    if (blob) return parseRefundRequestDetails(blob);
    if (ticket.action_required) {
      return {
        bookingId: ticket.booking_id ? String(ticket.booking_id) : "",
        pnr: ticket.pnr || "",
        route: "",
        customerName: ticket.customer_name || "",
        customerEmail: ticket.customer_email || "",
        customerPhone: ticket.customer_phone || "",
      };
    }
    return null;
  }, [ticket]);

  useEffect(() => {
    if (!passengers.length || !ticket) return;
    if ((ticket.query_type || "").toUpperCase() !== "CANCELLATION") return;

    const withIds = passengers.filter((p) => p.passenger_id != null);

    // Priority 1: use pending_passenger_ids set by the bot (exact IDs, most reliable)
    // Guard against JSON string (some MySQL drivers return JSON columns as strings)
    let rawPending = ticket.pending_passenger_ids;
    if (typeof rawPending === "string") {
      try { rawPending = JSON.parse(rawPending); } catch { rawPending = null; }
    }
    const pendingIds = Array.isArray(rawPending) ? rawPending : null;
    if (pendingIds && pendingIds.length > 0) {
      const pendingSet = new Set(pendingIds.map(String));
      const autoSelected = withIds
        .filter((p) => pendingSet.has(String(p.passenger_id)))
        .map((p) => p.passenger_id);
      if (autoSelected.length > 0) {
        setSelectedPassengers(autoSelected);
        // Auto-switch to partial when the bot pre-selected a strict subset of passengers
        if (withIds.length > 1 && autoSelected.length < withIds.length) {
          setCancelType(2);
          setCancelTypeLabel("Partial Cancellation (Per Passenger)");
        }
        return;
      }
    }

    // Priority 2: fallback — parse passenger names from the action_required text
    const blob = findCancellationRequestText(ticket);
    if (!blob) return;

    const details = parseCancellationRequestDetails(blob);
    if (!details || !details.passengerNames.length) return;
    if (details.cancelType !== "partial") return;

    if (withIds.length <= 1) return;

    const autoSelected = passengers
      .filter((p) => {
        const fullName = [p.title, p.first_name, p.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        return details.passengerNames.some((reqName) =>
          reqName.includes(fullName) || fullName.includes(reqName)
        );
      })
      .map((p) => p.passenger_id)
      .filter(Boolean);

    if (autoSelected.length > 0) {
      setSelectedPassengers(autoSelected);
      if (autoSelected.length < withIds.length) {
        setCancelType(2);
        setCancelTypeLabel("Partial Cancellation (Per Passenger)");
      }
    }
  }, [passengers, ticket]);

  const passengersWithIds = useMemo(
    () => passengers.filter((p) => p.passenger_id != null),
    [passengers]
  );

  // When the customer picked specific passenger(s) to cancel via the chatbot,
  // `pending_passenger_ids` carries the exact IDs. Lock the selection to that
  // choice so an agent can't accidentally cancel a different passenger than
  // the one the customer actually requested.
  const customerLockedPassengerIds = useMemo(() => {
    if (!ticket || (ticket.query_type || "").toUpperCase() !== "CANCELLATION") return null;
    let rawPending = ticket.pending_passenger_ids;
    if (typeof rawPending === "string") {
      try { rawPending = JSON.parse(rawPending); } catch { rawPending = null; }
    }
    if (!Array.isArray(rawPending) || rawPending.length === 0) return null;
    return new Set(rawPending.map(String));
  }, [ticket]);

  const isSinglePassengerBooking = useMemo(
    () => passengersWithIds.length <= 1,
    [passengersWithIds]
  );

  const cancellationTypeSelectOptions = useMemo(
    () =>
      isSinglePassengerBooking
        ? CANCELLATION_TYPE_SELECT.filter((o) => o.cancelType === 1)
        : CANCELLATION_TYPE_SELECT,
    [isSinglePassengerBooking]
  );

  /** Error message when partial cancel (type 2) selection is invalid; null if OK or not partial. */
  const partialCancelSelectionError = useMemo(() => {
    if (cancelType !== 2) return null;
    const selected = selectedPassengers.filter((id) =>
      passengersWithIds.some((p) => p.passenger_id === id)
    );
    if (selected.length < 1) {
      return "Select at least one passenger for partial cancellation.";
    }
    if (
      passengersWithIds.length > 0 &&
      selected.length >= passengersWithIds.length
    ) {
      return "Partial cancellation cannot include every passenger. Use a full cancellation type instead.";
    }
    return null;
  }, [cancelType, selectedPassengers, passengersWithIds]);

  /** Selection rules for the quick "Cancel Selected Passengers" action (always partial). */
  const inlinePartialSelectionError = useMemo(() => {
    const selected = selectedPassengers.filter((id) =>
      passengersWithIds.some((p) => p.passenger_id === id)
    );
    if (selected.length < 1) {
      return "Select at least one passenger.";
    }
    if (
      passengersWithIds.length > 0 &&
      selected.length >= passengersWithIds.length
    ) {
      return "Cannot select every passenger for this action. Use full cancellation in the Cancellation section instead.";
    }
    return null;
  }, [selectedPassengers, passengersWithIds]);

  useEffect(() => {
    if (isSinglePassengerBooking && cancelType === 2) {
      setCancelType(1);
      setCancelTypeLabel(DEFAULT_CANCELLATION_TYPE_LABEL);
    }
  }, [isSinglePassengerBooking, cancelType]);

  useEffect(() => {
    if (isSinglePassengerBooking) {
      setSelectedPassengers([]);
    }
  }, [isSinglePassengerBooking]);

  useEffect(() => {
    console.log("[useEffect] ticket.status:", ticket?.status, "| statusSaving:", statusSaving);
    if (ticket && !statusSaving) {
      console.log("Setting statusValue to:", ticket.status);
      setStatusValue(ticket.status || "Open");
    }
  }, [ticket]);

  useEffect(() => {
    if (!ticket_id) {
      setLoading(false);
      setTicket(null);
      setBookings({ past: [], current: [], upcoming: [] });
      setPassengers([]);
      setPassengersLoading(false);
      setSelectedPassengers([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErr("");

    const load = async () => {
      try {
    setPassengersLoading(true);
    setPassengers([]);
    setSelectedPassengers([]);
    const [ticketRes, bookingsRes, passengersRes] = await Promise.all([
          api.post("/admin/tickets/detail", { ticket_id }),
          api.post("/admin/tickets/bookings", { ticket_id }),
          api.post("/admin/tickets/passengers", { ticket_id }).catch(() => ({
            data: { data: [] },
          })),
        ]);

        if (cancelled) return;

        const ticketData = normalizeTicketDetail(ticketRes.data?.data ?? null);
        // The second flight of a Whole Trip lives inside the first flight's ticket.
        if (
          ticketData?.linked_ticket_id &&
          Number(ticket_id) > Number(ticketData.linked_ticket_id)
        ) {
          navigate(`/admin/tickets/${ticketData.linked_ticket_id}`, { replace: true });
          return;
        }
        setTicket(ticketData);
        setCompanionTicket(null);

        if (ticketData?.pnr) {
          api
            .post("/admin/tickets/list", {
              search: ticketData.pnr,
              status: "Open",
              limit: 10,
              page: 1,
            })
            .then((res) => {
              if (cancelled) return;
              const others = (res.data?.data || []).filter(
                (t) => t.ticket_id !== Number(ticket_id)
              );
              setDuplicateTickets(others);
            })
            .catch(() => {});
        }

        setBookings(bucketBookings(bookingsRes.data?.data));

        const rawPassengers = passengersRes.data?.data;
        setPassengers(Array.isArray(rawPassengers) ? rawPassengers : []);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e.message || "Failed to load ticket";
          setErr(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) {
          setPassengersLoading(false);
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [ticket_id, navigate]);

  // Auto-refresh ticket when customer confirms via push notification (SSE → browser event)
  useEffect(() => {
    const handler = async (e) => {
      if (e.detail?.ticket_id !== Number(ticket_id)) return;
      try {
        const refreshRes = await api.post("/admin/tickets/detail", { ticket_id });
        setTicket(normalizeTicketDetail(refreshRes.data?.data ?? null));
      } catch { /* ignore refresh errors */ }
    };
    window.addEventListener("navigatio:ticket-update", handler);
    return () => window.removeEventListener("navigatio:ticket-update", handler);
  }, [ticket_id]);

  // Full booking detail (search_payload/actual_payload/fare_info/fare_detail) for the
  // expanded "Full Booking Detail" section — only fetched when a booking is linked.
  useEffect(() => {
    if (!ticket?.booking_id) {
      setLinkedBookingDetail(null);
      setLinkedBookingDetailError("");
      setLinkedBookingDetailLoading(false);
      return;
    }
    let cancelled = false;
    setLinkedBookingDetailLoading(true);
    setLinkedBookingDetailError("");
    api
      .get(`/admin/bookings/${ticket.booking_id}`)
      .then((res) => {
        if (cancelled) return;
        setLinkedBookingDetail(res.data?.data ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        setLinkedBookingDetailError(
          e?.response?.data?.message || e.message || "Failed to load full booking detail"
        );
      })
      .finally(() => {
        if (!cancelled) setLinkedBookingDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticket?.booking_id]);

  // Auto-fill refund form from booking data
  useEffect(() => {
    if (refundChargeSubmitted) return;
    const upcoming = bookings?.upcoming || [];
    const current = bookings?.current || [];
    const b = [...upcoming, ...current][0];
    if (!b) return;
    const totalFare  = b.totalfare        ? Number(b.totalfare)        : 0;
    const convFee    = b.convenience_fee  ? Number(b.convenience_fee)  : 0;
    const navcash    = Number(b.is_wallet_applied) === 1 ? (Number(b.wallet_points)  || 0) : 0;
    const giftcard   = (b.is_giftcard_applied === true || Number(b.is_giftcard_applied) === 1)
                         ? (Number(b.applied_giftcard_amount) || 0) : 0;
    const promo      = Number(b.applied_promo_code) === 1 ? (Number(b.applied_promo_code_amount) || 0) : 0;
    if (!totalFare && !convFee) return;
    setRefundChargeForm((prev) => {
      if (prev.total_amount_paid) return prev;
      const cancelCharge = parseFloat(prev.cancellation_charge) || 0;
      // Cash refund = totalFare - airline_charge - convenience_fee - navcash_used
      // (matches computeRefundBreakdown formula; navcash is reverted separately)
      const cashRefund = totalFare > 0
        ? String(Math.max(0, totalFare - cancelCharge - convFee - navcash))
        : "";
      return {
        ...prev,
        total_amount_paid: totalFare  ? String(totalFare)  : "",
        convenience_fee:   prev.convenience_fee || (convFee   ? String(convFee)   : ""),
        navcash_amount:    navcash    ? String(navcash)    : "",
        giftcard_amount:   giftcard   ? String(giftcard)   : "",
        promo_code_amount: promo      ? String(promo)      : "",
        refund_amount:     totalFare  ? cashRefund         : prev.refund_amount,
      };
    });
  }, [bookings, refundChargeSubmitted]);

  const getTicketDisplayValue = (key, data) => {
    if (!data) return "—";
    if (key === "zoho_ticket_id") {
      return formatVal(data.zoho_ticket_id) || "—";
    }
    if (key === "customer_name") {
      return (data.customer_name ?? [data.first_name, data.last_name].filter(Boolean).join(" ")) || "—";
    }
    if (key === "customer_email") {
      return formatVal(data.customer_email ?? data.email);
    }
    if (key === "customer_phone") {
      return formatVal(data.customer_phone ?? data.phone);
    }
    if (key === "assignedAgent") {
      const a = data.assignedAgent ?? data.assigned_agent;
      if (a == null) return "—";
      if (typeof a === "string") return a;
      const name = [a.first_name, a.last_name].filter(Boolean).join(" ").trim();
      if (name) return a.email ? `${name} (${a.email})` : name;
      if (a.email) return String(a.email);
      return "—";
    }
    if (key === "created_at") {
      return formatDate(data.created_at ?? data.createdAt);
    }
    
    return formatVal(data[key]);
  };

  const bookingTabContent = {
    past: <BookingsTabTable bookings={bookings.past} emptyMessage="No past bookings." />,
    current: <BookingsTabTable bookings={bookings.current} emptyMessage="No current bookings." />,
    upcoming: (
      <BookingsTabTable
        bookings={bookings.upcoming}
        emptyMessage="No upcoming bookings."
        showPayment={true}
      />
    ),
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-600">Loading...</div>;
  }

  if (err || !ticket) {
    return (
      <div>
        <div className="flex items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-bold">Ticket</h1>
          <button
            type="button"
            onClick={() => navigate("/admin/tickets")}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            Back to List
          </button>
        </div>
        <div className="text-sm text-red-600">{err || "Ticket not found."}</div>
      </div>
    );
  }

  const formatActionRequired = (text) => {
    if (!text) return [];
    const sections = [];
    let current = null;

    text.split("\n").forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) return;

      if (
        line.startsWith("Flight Details") ||
        line.startsWith("✈") ||
        line.startsWith("- PNR:") ||
        line.startsWith("- Route:") ||
        line.startsWith("- Customer:") ||
        line.startsWith("- Email:") ||
        line.startsWith("- Phone:")
      ) {
        if (!current || current.title !== "✈ Flight Details") {
          current = { title: "✈ Flight Details", items: [] };
          sections.push(current);
        }
        if (line.startsWith("- ")) {
          current.items.push(line.substring(2));
        }
      } else if (
        line.startsWith("Charges Shown") ||
        line.startsWith("- Total Fare") ||
        line.startsWith("- Estimated Cancellation") ||
        line.startsWith("- Convenience Fee") ||
        line.startsWith("- Estimated Refund") ||
        line.startsWith("- Was Estimated")
      ) {
        if (!current || current.title !== "💰 Charges Shown to Customer") {
          current = { title: "💰 Charges Shown to Customer", items: [] };
          sections.push(current);
        }
        if (line.startsWith("- ")) {
          current.items.push(line.substring(2));
        }
      } else if (
        line.startsWith("⚠️ DGCA") ||
        line.startsWith("DGCA Check") ||
        line.startsWith("Booking Source") ||
        line.startsWith("Please verify") ||
        line.startsWith("This booking appears") ||
        // TODO: Do not change this prefix — it matches legacy ticket history lines from older flows.
        // Future history writes should use new wording; if migrating, update this matcher in sync.
        line.startsWith("Please check with TBO") ||
        line.startsWith("Note: Our cancellation") ||
        // Actual current wording from cancellation.service.js's dgcaAgentNote ("Our
        // convenience fee is NOT refundable...") — kept alongside the legacy
        // "Note: Our cancellation" prefix above rather than replacing it.
        line.startsWith("Note: Our convenience") ||
        // New: the DGCA-hold ticket note now explicitly says not to process until
        // the "Confirm TBO Refund" admin action has been used (tbo_dgca_confirmed).
        line.startsWith("DO NOT process this cancellation") ||
        // TODO: Do not change this prefix — it matches legacy ticket history lines from older flows.
        // Future history writes should use new wording; if migrating, update this matcher in sync.
        line.startsWith("Mention to TBO")
      ) {
        if (!current || current.title !== "⚠️ DGCA Information") {
          current = { title: "⚠️ DGCA Information", items: [] };
          sections.push(current);
        }
        current.items.push(line);
      } else if (line.startsWith("Remarks:")) {
        current = {
          title: "📝 Remarks",
          items: [line.replace("Remarks:", "").trim()],
        };
        sections.push(current);
      } else {
        if (!current) {
          current = { title: null, items: [] };
          sections.push(current);
        }
        current.items.push(line);
      }
    });

    return sections;
  };

  const refreshTicketDetail = async () => {
    const res = await api.post("/admin/tickets/detail", {
      ticket_id: Number(ticket_id),
    });
    setTicket(normalizeTicketDetail(res.data?.data ?? null));
  };

  const handleEnterBookingFailedPnr = async () => {
    const pnrTrim = bookingFailedPnr.trim();
    const tboBookingIdTrim = bookingFailedTboBookingId.trim();
    if (!pnrTrim && !tboBookingIdTrim) return;

    setBookingFailedResult(null);
    setBookingFailedPnrLoading(true);
    try {
      const res = await api.post(
        "/admin/tickets/confirm-booking-pnr",
        {
          ticket_id: Number(ticket_id),
          ...(pnrTrim ? { pnr: pnrTrim.toUpperCase() } : {}),
          ...(tboBookingIdTrim ? { tbo_booking_id: tboBookingIdTrim } : {}),
        }
      );
      toast.success(
        `Booking recovered! PNR: ${res.data?.data?.pnr || pnrTrim || "—"}. Ticket closed.`
      );
      const detailRes = await api.post(
        "/admin/tickets/detail",
        { ticket_id: Number(ticket_id) }
      );
      setTicket(detailRes.data?.data);
      setBookingFailedPnr("");
      setBookingFailedTboBookingId("");
    } catch (e) {
      const status = e?.response?.status;
      const reason = e?.response?.data?.reason;
      const message = e?.response?.data?.message || "Failed to confirm booking";

      // If both fields were submitted, make clear TBO was asked to match both —
      // otherwise the message alone can read as if only one field was checked.
      const checkedParts = [];
      if (pnrTrim) checkedParts.push(`PNR "${pnrTrim.toUpperCase()}"`);
      if (tboBookingIdTrim) checkedParts.push(`TBO Booking ID "${tboBookingIdTrim}"`);
      const checkedLine = checkedParts.length ? `Checked: ${checkedParts.join(" and ")}.` : "";

      if (reason === "TBO_LOOKUP_FAILED") {
        setBookingFailedResult({ type: "lookup_failed", message, checkedLine });
      } else if (reason === "NOT_TICKETED") {
        setBookingFailedResult({ type: "not_ticketed", message });
      } else if (reason === "PASSENGER_NAME_MISMATCH") {
        setBookingFailedResult({ type: "name_mismatch" });
      } else if (status === 409) {
        setBookingFailedResult({ type: "in_progress", message });
      } else {
        toast.error(message);
      }
    } finally {
      setBookingFailedPnrLoading(false);
    }
  };

  // ── Failed-ticket booking edit handlers ──────────────────────────────────
  const openEditBookingModal = () => {
    const b = [...(bookings?.upcoming || []), ...(bookings?.current || [])][0];
    setEditBookingFlightId(b?.flight_id ?? "");
    setEditBookingPnr(b?.pnr ?? "");
    setEditBookingAirlinePnr(b?.airline_pnr ?? "");
    setEditBookingErrors({ flightId: "", pnr: "" });
    setEditBookingNameMismatch(null);
    setEditBookingOpen(true);
  };

  const closeEditBookingModal = () => {
    setEditBookingOpen(false);
    setEditBookingConfirmOpen(false);
    setEditBookingNameMismatch(null);
  };

  const handleEditBookingSubmit = (e) => {
    e.preventDefault();
    const flightIdTrim   = (editBookingFlightId ?? "").trim();
    const pnrTrim        = (editBookingPnr ?? "").trim();
    const airlinePnrTrim = (editBookingAirlinePnr ?? "").trim();
    const errors = { flightId: "", pnr: "" };
    if (!flightIdTrim) errors.flightId = "TBO Booking ID is required";
    if (!pnrTrim && !airlinePnrTrim) errors.pnr = "PNR or Airline PNR is required";
    if (errors.flightId || errors.pnr) { setEditBookingErrors(errors); return; }
    setEditBookingConfirmOpen(true);
  };

  const handleEditBookingConfirm = async () => {
    const b = [...(bookings?.upcoming || []), ...(bookings?.current || [])][0];
    if (!b?.booking_id) { toast.error("Booking not loaded"); return; }
    setEditBookingSaving(true);
    try {
      await api.patch(`/admin/bookings/${b.booking_id}/pnr`, {
        flight_id:   (editBookingFlightId ?? "").trim() || null,
        pnr:         (editBookingPnr ?? "").trim()       || null,
        airline_pnr: (editBookingAirlinePnr ?? "").trim() || null,
      });
      toast.success("Booking details updated successfully");
      closeEditBookingModal();
      // Refresh bookings data
      const res = await api.post("/admin/tickets/bookings", { ticket_id: Number(ticket_id) });
      setBookings(bucketBookings(res.data?.data));
    } catch (err) {
      const reason = err?.response?.data?.reason;
      if (reason === "NAME_MISMATCH") {
        setEditBookingNameMismatch({
          mismatches: err.response.data.mismatches || [],
          tboPassengers: err.response.data.tbo_passengers || [],
        });
      } else {
        toast.error(err?.response?.data?.message || err?.message || "Update failed");
      }
    } finally {
      setEditBookingSaving(false);
    }
  };

  const handleInstantRefund = async () => {
    const upcoming = bookings?.upcoming || [];
    const current = bookings?.current || [];
    const b = [...upcoming, ...current][0];

    if (!b) {
      toast.error(
        "Booking details not loaded. " +
        "Please wait and try again."
      );
      return;
    }

    if (!b.booking_id) {
      toast.error(
        "No booking ID found."
      );
      return;
    }

    const fareAmount = Number(b.totalfare || 0);
    if (fareAmount <= 0) {
      toast.error("Invalid fare amount for refund.");
      return;
    }

    const confirmed = window.confirm(
      `Issue an instant refund of ${formatINR(fareAmount)} to the customer? ` +
        `This will send the full amount back to their original payment method and cannot be undone.`
    );
    if (!confirmed) return;

    setInstantRefundLoading(true);
    try {
      const res = await api.post(
        "/admin/tickets/instant-refund",
        {
          ticket_id: Number(ticket_id),
          booking_id: b.booking_id,
        }
      );
      if (res.data?.success && !res.data?.partial_success) {
        toast.success(
          `Instant refund of ${formatINR(fareAmount)} completed. The customer has been notified.`
        );
        const refreshRes = await api.post(
          "/admin/tickets/detail",
          { ticket_id: Number(ticket_id) }
        );
        setTicket(normalizeTicketDetail(refreshRes.data?.data ?? null));
      } else if (res.data?.partial_success) {
        toast.error(
          "The instant refund partially completed. The cash refund to the customer's payment method failed and the ticket has been flagged for follow-up."
        );
        const refreshed = await api.post("/admin/tickets/detail", {
          ticket_id: Number(ticket_id),
        });
        setTicket(normalizeTicketDetail(refreshed.data?.data ?? null));
      } else {
        toast.error(
          res.data?.message || "Could not process the refund. Please try again."
        );
      }
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 207 && data?.partial_success) {
        toast.error(
          data?.message ||
            "The instant refund partially completed. The cash refund to the customer's payment method failed and the ticket has been flagged for follow-up."
        );
        api
          .post("/admin/tickets/detail", { ticket_id: Number(ticket_id) })
          .then((refreshed) => {
            setTicket(normalizeTicketDetail(refreshed.data?.data ?? null));
          })
          .catch(() => {});
      } else if (status === 409) {
        toast.error(
          data?.message || "This action is already in progress. Please refresh the page."
        );
      } else {
        toast.error(
          data?.message ||
            e?.message ||
            "Could not process the refund. Please try again."
        );
      }
    } finally {
      setInstantRefundLoading(false);
    }
  };

  const handleRetryFailedRefund = async () => {
    if (
      !window.confirm(
        "Retry the failed refund? The system will attempt to send the cash refund to the customer again."
      )
    ) {
      return;
    }
    setRetryLoading(true);
    try {
      const res = await api.post("/admin/tickets/retry-failed-refund", {
        ticket_id: Number(ticket_id),
      });
      if (res.data?.success) {
        toast.success(res.data.message || "Refund retried successfully.");
        const refreshed = await api.post("/admin/tickets/detail", {
          ticket_id: Number(ticket_id),
        });
        setTicket(normalizeTicketDetail(refreshed.data?.data ?? null));
      } else if (res.data?.partial_success) {
        toast.error(
          "The retry could not complete the cash refund. Wallet and promo code updates are done, but the cash refund still failed. Please contact technical support if this continues."
        );
        const refreshed = await api.post("/admin/tickets/detail", {
          ticket_id: Number(ticket_id),
        });
        setTicket(normalizeTicketDetail(refreshed.data?.data ?? null));
      } else {
        toast.error(
          res.data?.message ||
            "Could not process the retry. Please try again."
        );
      }
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 207 && data?.partial_success) {
        toast.error(
          "The retry could not complete the cash refund. Wallet and promo code updates are done, but the cash refund still failed. Please contact technical support if this continues."
        );
        const refreshed = await api.post("/admin/tickets/detail", {
          ticket_id: Number(ticket_id),
        });
        setTicket(normalizeTicketDetail(refreshed.data?.data ?? null));
      } else if (status === 409) {
        toast.error(
          data?.message || "A refund is already in progress for this ticket."
        );
      } else if (
        status === 400 &&
        String(data?.message || e?.message || "").includes(
          "not currently flagged for retry"
        )
      ) {
        toast.success(
          data?.message ||
            e?.message ||
            "This ticket is not currently flagged for retry."
        );
        try {
          await refreshTicketDetail();
        } catch {
          /* non-blocking */
        }
      } else {
        toast.error(
          data?.message || e.message || "Could not process the retry. Please try again."
        );
      }
    } finally {
      setRetryLoading(false);
    }
  };

  // Cancellation and reschedule tickets get a work-first layout (see the render below).
  const isCancellationLayout =
    !ticketHasNoPnr && (ticket.query_type || "").toUpperCase() === "CANCELLATION";
  const isRescheduleLayout =
    !ticketHasNoPnr && (ticket.query_type || "").toUpperCase() === "RESCHEDULING";

  // ── Page blocks: each section once; the layouts below only arrange them. ──
  const blkTicketCustomer = (
    <>
      <div className="border rounded-2xl overflow-hidden bg-white">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
          Ticket &amp; Customer
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TICKET_TOP_FIELDS.map(({ key, label }) => {
              // Long values (an email address) take the full row and wrap, so
              // they never spill out of the box in the narrow side column.
              const isEmail = /email/i.test(key);
              const value = key === "pnr" ? ticket.pnr || "—" : getTicketDisplayValue(key, ticket);
              return (
                <div key={key} className={`flex flex-col min-w-0 ${isEmail ? "md:col-span-2" : ""}`}>
                  <label className="text-sm font-semibold text-gray-600">{label}</label>
                  <div
                    className="mt-1 p-3 rounded-xl bg-gray-50 text-sm border min-h-[44px] break-words [overflow-wrap:anywhere]"
                    title={typeof value === "string" ? value : undefined}
                  >
                    {value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
  const blkBookings = (
    <>
      {!ticketHasNoPnr &&
        ((bookings.upcoming?.length > 0 ||
        bookings.current?.length > 0 ||
        bookings.past?.length > 0) ||
        ["RESCHEDULING", "REFUND"].includes((ticket.query_type || "").toUpperCase())) && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            Bookings
          </div>
          <div className="border-b border-gray-200">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {["upcoming", "current", "past"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setBookingTab(tab)}
                  className={`flex-1 px-4 py-2 text-sm font-semibold capitalize ${
                    bookingTab === tab ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab === "past" ? "Past Bookings" : tab === "current" ? "Current Bookings" : "Upcoming Bookings"}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-[120px]">
            {bookingTabContent[bookingTab]}
          </div>
        </div>
      )}
    </>
  );
  const blkPassengers = (
    <>
      {!ticketHasNoPnr && (
        passengers.length > 0 ||
        ["RESCHEDULING", "REFUND"].includes((ticket.query_type || "").toUpperCase())
      ) ? (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            Passengers
          </div>
          {passengers.length === 0 && !passengersLoading ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              No passenger data found for this booking.
            </div>
          ) : passengersLoading ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">Loading passengers…</div>
          ) : null}
          {ticket.query_type === "CANCELLATION" &&
          isSinglePassengerBooking &&
          passengersWithIds.length === 1 ? (
            <div className="px-4 pb-2 pt-1 text-xs text-gray-600 border-b border-gray-100 bg-white">
              This booking has a single passenger. Use the Full Cancellation flow on
              the right panel to cancel the booking.
            </div>
          ) : null}
          {ticket.query_type === "CANCELLATION" &&
          customerLockedPassengerIds != null ? (
            <div className="px-4 pb-2 pt-1 text-xs text-amber-700 border-b border-gray-100 bg-amber-50">
              {passengersWithIds.length > 0 &&
              passengersWithIds.every((p) => customerLockedPassengerIds.has(String(p.passenger_id)))
                ? "The customer requested cancellation for all passengers via chat."
                : "The customer requested cancellation for specific passenger(s) via chat."}{" "}
              Selection is locked to their request and cannot be changed here.
            </div>
          ) : null}
          {passengers.length > 0 && <div className="min-h-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead className="border-t bg-white">
                <tr className="text-left">
                  {ticket.query_type === "CANCELLATION" &&
                  !isSinglePassengerBooking ? (
                    <th className="px-4 py-3 w-10 text-left">Select</th>
                  ) : null}
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Lead Passenger</th>
                  <th className="px-4 py-3">Passenger ID</th>
                </tr>
              </thead>
              <tbody className="border-t">
                {passengers.map((p, idx) => {
                  const name = [p.title, p.first_name, p.last_name]
                    .filter((x) => x != null && String(x).trim() !== "")
                    .join(" ")
                    .trim();
                  const pid = p.passenger_id;
                  const isCancellation = ticket.query_type === "CANCELLATION";
                  const isSelected =
                    pid != null && selectedPassengers.includes(pid);
                  return (
                    <tr
                      key={p.passenger_id ?? p.pax_id ?? `passenger-${idx}`}
                      className="border-t hover:bg-gray-50"
                    >
                      {isCancellation && !isSinglePassengerBooking ? (
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
                            checked={isSelected}
                            disabled={customerLockedPassengerIds != null}
                            onChange={(e) => {
                              if (pid == null) return;
                              setSelectedPassengers((prev) =>
                                e.target.checked
                                  ? prev.includes(pid)
                                    ? prev
                                    : [...prev, pid]
                                  : prev.filter((id) => id !== pid)
                              );
                            }}
                            aria-label={`Select ${name || "passenger"}`}
                            title={
                              customerLockedPassengerIds != null
                                ? "Locked to the passenger(s) the customer selected for cancellation"
                                : undefined
                            }
                          />
                        </td>
                      ) : null}
                      <td className="px-4 py-3 font-semibold">
                        {name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {formatVal(p.pax_type_label)}
                      </td>
                      <td className="px-4 py-3">
                        {p.is_lead_pax ? "✓" : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {p.pax_id != null && String(p.pax_id).trim() !== ""
                          ? formatVal(p.pax_id)
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {ticket.query_type === "CANCELLATION" &&
            !isSinglePassengerBooking &&
            passengers.some(
              (p) =>
                p.passenger_id != null &&
                selectedPassengers.includes(p.passenger_id) &&
                p.is_lead_pax
            ) ? (
              <div className="mx-4 mb-3 mt-1 rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                ⚠️ Warning: You have selected the lead passenger. Cancelling the
                lead passenger may result in cancellation of the entire booking
                depending on the airline policy. Please verify with the airline system before
                proceeding.
              </div>
            ) : null}
            {ticket.query_type === "CANCELLATION" &&
            !isDirectionCancelTicket(ticket) &&
            !isSinglePassengerBooking &&
            selectedPassengers.length > 0 ? (
              <div className="p-4 pt-0">
                <button
                  type="button"
                  disabled={inlinePartialSelectionError != null}
                  title={inlinePartialSelectionError || undefined}
                  onClick={async () => {
                    const selectedIds = selectedPassengers.filter((id) =>
                      passengersWithIds.some((p) => p.passenger_id === id)
                    );
                    if (inlinePartialSelectionError) {
                      toast.error(inlinePartialSelectionError);
                      return;
                    }
                    const selectedPassengerNames = passengers
                      .filter(
                        (p) =>
                          p.passenger_id != null &&
                          selectedPassengers.includes(p.passenger_id)
                      )
                      .map((p) =>
                        [p.title, p.first_name, p.last_name]
                          .filter(Boolean)
                          .join(" ")
                      )
                      .join(", ");
                    const selectedPaxIds = passengers
                      .filter(
                        (p) =>
                          p.passenger_id != null &&
                          selectedPassengers.includes(p.passenger_id)
                      )
                      .map((p) => p.pax_id || p.passenger_id)
                      .join(", ");
                    const ok = window.confirm(
                      `Cancel ticket for: ${selectedPassengerNames}? This will process partial cancellation with the airline system.`
                    );
                    if (!ok) return;
                    const refreshAfterPartialCancel = async () => {
                      const [ticketRes, bookingsRes, passengersRes] =
                        await Promise.all([
                          api.post("/admin/tickets/detail", { ticket_id }),
                          api.post("/admin/tickets/bookings", { ticket_id }),
                          api
                            .post("/admin/tickets/passengers", { ticket_id })
                            .catch(() => ({ data: { data: [] } })),
                        ]);
                      const ticketData = normalizeTicketDetail(
                        ticketRes.data?.data ?? null
                      );
                      setTicket(ticketData);
                      setBookings(bucketBookings(bookingsRes.data?.data));
                      const rawPassengers = passengersRes.data?.data;
                      setPassengers(
                        Array.isArray(rawPassengers) ? rawPassengers : []
                      );
                    };

                    try {
                      const res = await api.post(
                        `/admin/tickets/${ticket_id}/cancel`,
                        {
                          cancel_type: 2,
                          remarks: `Partial cancellation for passengers: ${selectedPassengerNames}. Pax IDs: ${selectedPaxIds}`,
                          passenger_ids: selectedIds,
                          bypass_state_check: true,
                        }
                      );
                      if (res.data?.success && !res.data?.partial_success) {
                        toast.success("Partial cancellation processed");
                        setSelectedPassengers([]);
                        await refreshAfterPartialCancel();
                      } else if (res.data?.partial_success) {
                        toast.error(
                          "The cancellation completed, but the cash refund to the customer's payment method failed and the ticket has been flagged for follow-up."
                        );
                        setSelectedPassengers([]);
                        await refreshAfterPartialCancel();
                      } else {
                        toast.error(
                          res.data?.message || "Cancellation failed"
                        );
                      }
                    } catch (err) {
                      const status = err?.response?.status;
                      const data = err?.response?.data;
                      if (status === 207 && data?.partial_success) {
                        toast.error(
                          data?.message ||
                            "The action partially completed. The cash refund to the customer's payment method failed."
                        );
                        setSelectedPassengers([]);
                        await refreshAfterPartialCancel();
                      } else if (status === 409) {
                        toast.error(
                          data?.message ||
                            "This action is already in progress. Please refresh the page."
                        );
                      } else {
                        toast.error(
                          data?.message ||
                            err?.message ||
                            "Cancellation failed"
                        );
                      }
                    }
                  }}
                  className="w-full rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
                >
                  Cancel Selected Passengers
                </button>
              </div>
            ) : null}
          </div>}
        </div>
      ) : null}
    </>
  );
  const blkBookingDetail = (
    <>
      {ticket.booking_id && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex items-center justify-between">
            <span>Full Booking Detail</span>
            {linkedBookingDetailLoading && (
              <span className="text-xs font-normal text-gray-500">Loading…</span>
            )}
          </div>
          {linkedBookingDetailError ? (
            <div className="px-4 py-6 text-sm text-red-600">{linkedBookingDetailError}</div>
          ) : !linkedBookingDetail && !linkedBookingDetailLoading ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              Booking detail not available.
            </div>
          ) : linkedBookingDetail ? (
            <div className="divide-y divide-gray-100">
              <div className="p-4">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Flight &amp; Segments
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200">
                  <BookingRowsTable rows={segmentsDetailRows} emptyMessage="No segment detail available." />
                </div>
              </div>

              <div className="p-4">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Passengers (Full Detail)
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200">
                  <BookingRowsTable rows={passengersDetailRows} emptyMessage="No passenger detail available." />
                </div>
              </div>

              <div className="p-4">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Fare Breakdown
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200 max-w-md">
                  {fareInfoDetailRows.length > 0 ? (
                    <table className="w-full text-sm">
                      <tbody>
                        {fareInfoDetailRows.map((row, idx) => {
                          const isTotalRow = String(row.label).toLowerCase() === "total";
                          return (
                            <tr key={idx} className={isTotalRow ? "bg-gray-100" : ""}>
                              <td className={`py-2 px-4 ${isTotalRow ? "font-bold text-gray-800" : "text-gray-700"}`}>
                                {row.label}
                              </td>
                              <td className={`py-2 px-4 text-right tabular-nums ${isTotalRow ? "font-bold text-gray-800" : "text-gray-700"}`}>
                                {formatINR(row.value)}
                              </td>
                            </tr>
                          );
                        })}
                        {!fareInfoDetailHasTotal && fareInfoDetailRows.length > 0 && (
                          <tr className="bg-gray-100">
                            <td className="py-2.5 px-4 font-bold text-gray-800">Total</td>
                            <td className="py-2.5 px-4 text-right font-bold text-gray-800 tabular-nums">
                              {formatINR(fareInfoDetailTotal)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-3 text-sm text-gray-600">No fare info available.</div>
                  )}
                </div>
              </div>

              <div className="p-4">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Payment
                </div>
                {linkedBookingSummary?.payment ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Method: </span>{linkedBookingSummary.payment.method || "-"}</div>
                    <div><span className="text-gray-500">Status: </span>{linkedBookingSummary.payment.payment_status || "-"}</div>
                    <div><span className="text-gray-500">Amount: </span>{formatINR(linkedBookingSummary.payment.amount)}</div>
                    <div>
                      <span className="text-gray-500">Paid At: </span>
                      {linkedBookingSummary.payment.paid_at ? new Date(linkedBookingSummary.payment.paid_at).toLocaleString() : "-"}
                    </div>
                    <div>
                      <span className="text-gray-500">Payment ID: </span>
                      {linkedBookingSummary.payment.payment_id ?? linkedBookingSummary.payment_id ?? "-"}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Payment ID: {linkedBookingSummary?.payment_id ?? linkedBookingDetail?.payment_id ?? "-"}
                  </div>
                )}
              </div>

              <div className="p-4">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Invoice
                </div>
                <div className="text-sm text-gray-700">
                  Invoice ID: {linkedBookingSummary?.invoice_id ?? "-"}
                </div>
              </div>

              <div className="p-4">
                <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Raw Booking Payloads
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!linkedBookingDetail?.search_payload}
                    onClick={() => setRawPayloadModal({ open: true, type: "search" })}
                  >
                    User Booking Data
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!linkedBookingDetail?.actual_payload}
                    onClick={() => setRawPayloadModal({ open: true, type: "actual" })}
                  >
                    Booking Passenger Details
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!linkedBookingDetail?.fare_detail}
                    onClick={() => setRawPayloadModal({ open: true, type: "fare_detail" })}
                  >
                    Search Fare Detail
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
  const blkHistory = (
    <>
      <TicketHistoryTimeline
        history={
          companionTicket
            ? [
                ...(ticket.history || []).map((h) => ({ ...h, action_taken: `[Flight 1] ${h.action_taken || h.action || ""}` })),
                ...(companionTicket.history || []).map((h) => ({ ...h, action_taken: `[Flight 2] ${h.action_taken || h.action || ""}` })),
              ].sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0))
            : ticket.history
        }
      />
    </>
  );
  const blkBookingFailedCustomer = (
    <>
      {(ticket.query_type || "").toUpperCase() === "BOOKING_FAILED" && (
        <CustomerHistoryCard
          priority
          customerHistory={ticket.customer_history}
          onViewBookings={
            ticket.customer_history?.user_id
              ? () => navigate(`/admin/bookings?user_id=${ticket.customer_history.user_id}`)
              : undefined
          }
        />
      )}
    </>
  );
  const blkStatus = (
    <>
      <div className="border rounded-2xl overflow-hidden bg-white">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
          Status
        </div>
        <div className="p-4 space-y-3">
          <StatusBadge status={statusValue || ticket.status} />
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-1">Change status</label>
            <select
              value={statusValue || ""}
              onChange={async (e) => {
                const newValue = e.target.value;
                console.log("Status dropdown changed to:", newValue);

                // Prevent selecting same value
                if (newValue === statusValue) return;

                if (newValue === "Closed") {
                  setPendingCloseStatus("Closed");
                  setShowClosingModal(true);
                  return;
                }

                const prev = statusValue;
                setStatusValue(newValue);
                setStatusSaving(true);
                console.log("[saving] statusSaving = true");
                try {
                  const res = await api.post("/admin/tickets/update-status", {
                    ticket_id,
                    status: newValue,
                  });
                  if (res.data?.success) {
                    toast.success("Status updated");
                    setTicket((t) => ({ ...t, status: newValue }));
                    setStatusValue(newValue);
                  } else {
                    throw new Error(res.data?.message || "Update failed");
                  }
                } catch (err) {
                  toast.error(err?.response?.data?.message || err?.message || "Update failed");
                  setStatusValue(prev);
                } finally {
                  setStatusSaving(false);
                  console.log("[saving] statusSaving = false");
                }
              }}
              disabled={statusSaving}
              className="mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
            >
              <option value="Open">Open</option>
              <option value="On Hold">On Hold</option>
              <option value="Escalated">Escalated</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          {statusSaving && <div className="text-xs text-gray-500">Saving...</div>}
        </div>
      </div>
    </>
  );
  const blkQueryType = (
    <>
      <div className="border rounded-2xl overflow-hidden bg-white">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
          Query Type
        </div>
        <div className="p-4">
          <QueryTypeBadge queryType={ticket.query_type} />
        </div>
      </div>
    </>
  );
  const blkDgca = (
    <>
      {!ticketHasNoPnr && Number(ticket.is_dgca_eligible) === 1 && (() => {
        const tboConfirmed = Number(ticket.tbo_dgca_confirmed) === 1;
        return (
          <div className={`border rounded-2xl overflow-hidden bg-white ${tboConfirmed ? "border-green-300" : "border-red-300"}`}>
            <div className={`px-4 py-3 text-sm font-semibold border-b flex items-center gap-2 ${tboConfirmed ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
              <span>{tboConfirmed ? "✅" : "🔒"}</span>
              <span className={tboConfirmed ? "text-green-800" : "text-red-800"}>
                DGCA 48-Hour Look-In Eligible — {tboConfirmed ? "TBO Confirmed" : "Action Required"}
              </span>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-sm text-gray-700">
                This booking appears eligible under the DGCA 48-hour look-in option. The
                customer has only been told their request was received — no charge
                breakdown has been shown to them.
              </p>
              {tboConfirmed ? (
                <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
                  <p className="font-semibold">
                    ✅ Confirmed with TBO by {ticket.tbo_dgca_confirmed_by_name || "an agent"}
                    {ticket.tbo_dgca_confirmed_at
                      ? ` on ${new Date(ticket.tbo_dgca_confirmed_at).toLocaleString("en-IN")}`
                      : ""}
                    .
                  </p>
                  {ticket.tbo_dgca_confirmation_note && (
                    <p className="italic mt-0.5">&ldquo;{ticket.tbo_dgca_confirmation_note}&rdquo;</p>
                  )}
                </div>
              ) : (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 font-semibold">
                  🔒 Not yet processable — go to the Cancellation tab and use &ldquo;Confirm TBO
                  Refund&rdquo; after checking with TBO for full airline refund eligibility.
                </div>
              )}
              <div className="rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-800 font-semibold">
                ⚠️ Our cancellation charges are NOT refundable in this case, unless the flight
                itself was not operational.
              </div>
              {ticket.dgca_check_reason && (
                <div className="text-xs text-gray-500 pt-1">
                  DGCA Check: {ticket.dgca_check_reason}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
  const blkCustomerHistory = (
    <>
      {ticket.customer_history &&
        (ticket.query_type || "").toUpperCase() !== "BOOKING_FAILED" && (
        <CustomerHistoryCard
          customerHistory={ticket.customer_history}
          onViewBookings={
            ticket.customer_history.user_id
              ? () => navigate(`/admin/bookings?user_id=${ticket.customer_history.user_id}`)
              : undefined
          }
        />
      )}
    </>
  );
  const blkCancellationStatus = (
    <>
      {!ticketHasNoPnr && ticket.cancellation_status && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            Cancellation Status
          </div>
          <div className="p-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              ticket.cancellation_status === "pending_charges"
                ? "bg-orange-100 text-orange-800"
                : ticket.cancellation_status === "charges_notified"
                ? "bg-blue-100 text-blue-800"
                : ticket.cancellation_status === "cancelled"
                ? "bg-red-100 text-red-800"
                : ticket.cancellation_status === "refund_processed"
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}>
              {ticket.cancellation_status
                .replace(/_/g, " ")
                .replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>
        </div>
      )}
    </>
  );
  const blkCancellationRequest = (
    <>
      {!ticketHasNoPnr && cancellationRequestDetails && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex flex-wrap items-center justify-between gap-2">
            <span>Cancellation Request Details</span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                cancellationRequestDetails.cancelType === "full"
                  ? "bg-red-100 text-red-800"
                  : "bg-orange-100 text-orange-800"
              }`}
            >
              {cancellationRequestDetails.cancelType === "full"
                ? "Full"
                : "Partial"}
            </span>
          </div>
          <div className="p-4 space-y-4">
            {cancellationRequestDetails.cancelType === "partial" &&
              cancellationRequestDetails.passengerNames.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-2">
                    Passengers Requested for Cancellation:
                  </div>
                  <ul className="text-sm text-gray-800 space-y-1">
                    {cancellationRequestDetails.passengerNames.map((name, idx) => (
                      <li key={`${name}-${idx}`} className="flex gap-2 items-start">
                        <span className="shrink-0" aria-hidden>
                          🚫
                        </span>
                        <span>{name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            {cancellationRequestDetails.customerDesc ? (
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2">
                  Customer Description
                </div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                  {cancellationRequestDetails.customerDesc}
                </div>
              </div>
            ) : null}
            <p className="text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
              ℹ️ Process cancellation for the above passengers using the Cancellation section below
            </p>
          </div>
        </div>
      )}
    </>
  );
  const blkReschedulingRequest = (
    <>
      {!ticketHasNoPnr && reschedulingRequestDetails && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex flex-wrap items-center justify-between gap-2">
            <span>Rescheduling Request Details</span>
            <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-900">
              Rescheduling
            </span>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex gap-2 items-start text-sm text-gray-800">
              <span className="shrink-0" aria-hidden>
                📋
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-600 mb-0.5">Reason</div>
                <div className="whitespace-pre-wrap">
                  {reschedulingRequestDetails.reason || "—"}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-start text-sm text-gray-800">
              <span className="shrink-0" aria-hidden>
                📅
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-600 mb-0.5">
                  Preferred Date
                </div>
                <div>{reschedulingRequestDetails.preferredDate || "—"}</div>
              </div>
            </div>
            <div className="flex gap-2 items-start text-sm text-gray-800">
              <span className="shrink-0" aria-hidden>
                ✈️
              </span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-600 mb-0.5">
                  Original Airline
                </div>
                <div>{reschedulingRequestDetails.preferredAirline || "—"}</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-2">Passengers</div>
              {(() => {
                const names = reschedulingRequestDetails.passengerNames.length > 0
                  ? reschedulingRequestDetails.passengerNames
                  : passengers
                      .map((p) =>
                        [p.title, p.first_name, p.last_name].filter(Boolean).join(" ").trim()
                      )
                      .filter(Boolean);
                return names.length > 0 ? (
                  <ul className="text-sm text-gray-800 space-y-1">
                    {names.map((name, idx) => (
                      <li key={`${name}-${idx}`} className="flex gap-2 items-start">
                        <span className="shrink-0" aria-hidden>👤</span>
                        <span>{name}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="flex gap-2 items-start text-sm text-gray-800">
                    <span className="shrink-0" aria-hidden>👤</span>
                    <span>—</span>
                  </div>
                );
              })()}
            </div>
            {reschedulingRequestDetails.customerDesc ? (
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2">
                  Customer Description
                </div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                  {formatISOTimestampsInText(reschedulingRequestDetails.customerDesc)}
                </div>
              </div>
            ) : null}
            <p className="text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
              ℹ️ Process rescheduling in the airline system using the details above, then log the
              outcome in the Rescheduling section below
            </p>
          </div>
        </div>
      )}
    </>
  );
  const blkRefundRequest = (
    <>
      {refundRequestDetails && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex flex-wrap items-center justify-between gap-2">
            <span>Refund Request Details</span>
            <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-800">
              {REFUND_STATUS_LABELS[ticket.cancellation_status] || ticket.cancellation_status?.replace(/_/g, " ") || "Pending"}
            </span>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {refundRequestDetails.bookingId && (
                <div>
                  <div className="text-xs text-gray-500">Booking ID</div>
                  <div className="font-semibold font-mono">{refundRequestDetails.bookingId}</div>
                </div>
              )}
              {refundRequestDetails.pnr && (
                <div>
                  <div className="text-xs text-gray-500">PNR</div>
                  <div className="font-semibold font-mono tracking-widest">{refundRequestDetails.pnr}</div>
                </div>
              )}
              {refundRequestDetails.route && (
                <div className="col-span-1 sm:col-span-2">
                  <div className="text-xs text-gray-500">Route</div>
                  <div className="font-semibold">{refundRequestDetails.route}</div>
                </div>
              )}
              {refundRequestDetails.customerName && (
                <div>
                  <div className="text-xs text-gray-500">Customer</div>
                  <div className="font-semibold">{refundRequestDetails.customerName}</div>
                </div>
              )}
              {refundRequestDetails.customerEmail && (
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-semibold break-all">{refundRequestDetails.customerEmail}</div>
                </div>
              )}
              {refundRequestDetails.customerPhone && (
                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="font-semibold">{refundRequestDetails.customerPhone}</div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
              ℹ️ Verify the cancellation charges in TBO, then notify the customer using the section below.
            </p>
          </div>
        </div>
      )}
    </>
  );
  const blkRefundProcessing = (
    <>
      {(ticket.query_type || "").toUpperCase() === "REFUND" && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            Refund Processing
          </div>
          <div className="p-4 space-y-6">

            {/* Customer Confirmation Status Banner */}
            {ticket.cancellation_status === "customer_accepted" ? (
              <div className="rounded-xl border border-green-300 bg-green-50 px-4 py-3 flex items-start gap-3">
                <span className="text-lg leading-none mt-0.5">✅</span>
                <div>
                  <div className="text-sm font-bold text-green-800">Customer Confirmed</div>
                  <div className="text-xs text-green-700 mt-0.5">
                    The customer has accepted the charges and confirmed cancellation.
                    {ticket.fee_accepted_at && (
                      <span className="ml-1">Confirmed at: {new Date(ticket.fee_accepted_at).toLocaleString("en-IN")}</span>
                    )}
                  </div>
                </div>
              </div>
            ) : ticket.cancellation_status === "charges_notified" ? (
              <div className="rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3">
                <span className="text-lg leading-none mt-0.5">⏳</span>
                <div>
                  <div className="text-sm font-bold text-yellow-800">Waiting for Customer Confirmation</div>
                  <div className="text-xs text-yellow-700 mt-0.5">
                    Charges notification sent. Waiting for customer to confirm via push notification.
                  </div>
                </div>
              </div>
            ) : null}

            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Step 1 — Get Charges from TBO
              </div>
              <button
                type="button"
                disabled={refundTboLoading}
                onClick={async () => {
                  setRefundTboLoading(true);
                  try {
                    const chargesBody = {
                      ticket_id: ticket.ticket_id,
                      cancel_type: 1,
                      passenger_ids: [],
                    };
                    const res = await api.post(
                      `/admin/tickets/${ticket.ticket_id}/cancellation-charges`,
                      chargesBody
                    );
                    const payload = res.data?.data ?? null;
                    setRefundTboCharges(payload);
                    toast.success(res.data?.message || "Charges loaded from TBO");
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.message || err?.message || "Failed to fetch charges"
                    );
                  } finally {
                    setRefundTboLoading(false);
                  }
                }}
                className="w-full rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {refundTboLoading ? "Loading…" : "Get Cancellation Charges from TBO"}
              </button>

              {refundTboCharges != null && (
                <div className="mt-3 space-y-2">
                  <div className="text-sm font-semibold text-gray-600">TBO Response</div>
                  <details>
                    <summary className="cursor-pointer text-xs font-semibold text-gray-500 hover:text-gray-700">
                      Technical details (developers only)
                    </summary>
                    <pre className="text-xs bg-gray-900 text-gray-100 rounded-xl p-3 overflow-x-auto max-h-64 overflow-y-auto">
                      {JSON.stringify(refundTboCharges.charges ?? refundTboCharges, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Step 2 — Notify Customer with Refund Breakdown
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Enter the cancellation charge from TBO. Convenience fee and refund amount are auto-filled from booking data. The customer will receive a push notification with the full breakdown.
              </p>
              <div className="space-y-3">

                {/* Total Amount Paid — read-only, from booking */}
                <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-500">Total Amount Paid by Customer</div>
                    <div className="text-base font-bold text-gray-800 mt-0.5">
                      {refundChargeForm.total_amount_paid
                        ? `₹${Number(refundChargeForm.total_amount_paid).toLocaleString("en-IN")}`
                        : <span className="text-gray-400 text-sm font-normal">Loading from booking…</span>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">auto</span>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">
                    Airline Cancellation Charge (₹) <span className="text-gray-400 font-normal text-xs">— enter from TBO</span>
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 1500"
                    value={refundChargeForm.cancellation_charge}
                    onChange={(e) => {
                      const charge   = parseFloat(e.target.value) || 0;
                      const convFee  = parseFloat(refundChargeForm.convenience_fee) || 0;
                      const total    = parseFloat(refundChargeForm.total_amount_paid) || 0;
                      const navcash  = parseFloat(refundChargeForm.navcash_amount) || 0;
                      const refund   = total > 0 ? String(Math.max(0, total - charge - convFee - navcash)) : "";
                      setRefundChargeForm((f) => ({
                        ...f,
                        cancellation_charge: e.target.value,
                        refund_amount: total > 0 ? refund : f.refund_amount,
                      }));
                    }}
                    disabled={refundChargeSubmitted || refundChargeSubmitting}
                    className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                  />
                </div>

                {/* Convenience fee */}
                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">
                    Convenience Fee (₹) <span className="text-gray-400 font-normal text-xs">— forfeited, auto-filled</span>
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 200"
                    value={refundChargeForm.convenience_fee}
                    onChange={(e) => {
                      const convFee = parseFloat(e.target.value) || 0;
                      const charge  = parseFloat(refundChargeForm.cancellation_charge) || 0;
                      const total   = parseFloat(refundChargeForm.total_amount_paid) || 0;
                      const navcash = parseFloat(refundChargeForm.navcash_amount) || 0;
                      const refund  = total > 0 ? String(Math.max(0, total - charge - convFee - navcash)) : "";
                      setRefundChargeForm((f) => ({
                        ...f,
                        convenience_fee: e.target.value,
                        refund_amount: total > 0 ? refund : f.refund_amount,
                      }));
                    }}
                    disabled={refundChargeSubmitted || refundChargeSubmitting}
                    className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                  />
                </div>

                {/* NavCash / Wallet */}
                {Number(refundChargeForm.navcash_amount) > 0 && (
                  <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-green-700 font-semibold">NavCash / Wallet Used</div>
                      <div className="text-sm font-bold text-green-800 mt-0.5">
                        ₹{Number(refundChargeForm.navcash_amount).toLocaleString("en-IN")}
                      </div>
                      <div className="text-xs text-green-600 mt-0.5">Will be reverted to customer's NavCash wallet</div>
                    </div>
                    <span className="text-xs text-green-600 bg-green-100 rounded-full px-2 py-0.5">reversal</span>
                  </div>
                )}

                {/* Gift Card */}
                {Number(refundChargeForm.giftcard_amount) > 0 && (
                  <div className="rounded-xl bg-purple-50 border border-purple-200 px-3 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-purple-700 font-semibold">Gift Card Used</div>
                      <div className="text-sm font-bold text-purple-800 mt-0.5">
                        ₹{Number(refundChargeForm.giftcard_amount).toLocaleString("en-IN")}
                      </div>
                      <div className="text-xs text-purple-600 mt-0.5">Will be reverted to customer's gift card</div>
                    </div>
                    <span className="text-xs text-purple-600 bg-purple-100 rounded-full px-2 py-0.5">reversal</span>
                  </div>
                )}

                {/* Promo Code */}
                {Number(refundChargeForm.promo_code_amount) > 0 && (
                  <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-3 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-yellow-700 font-semibold">Promo Code Discount</div>
                      <div className="text-sm font-bold text-yellow-800 mt-0.5">
                        ₹{Number(refundChargeForm.promo_code_amount).toLocaleString("en-IN")}
                      </div>
                      <div className="text-xs text-yellow-600 mt-0.5">Forfeited on cancellation — not refunded</div>
                    </div>
                    <span className="text-xs text-yellow-600 bg-yellow-100 rounded-full px-2 py-0.5">forfeited</span>
                  </div>
                )}

                {/* Cash refund */}
                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">
                    Cash Refund to Customer (₹)
                    <span className="ml-1 text-xs font-normal text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">auto-calculated</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Auto-calculated"
                    value={refundChargeForm.refund_amount}
                    readOnly
                    disabled={refundChargeSubmitted}
                    className="w-full border rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-700 font-semibold outline-none cursor-default disabled:opacity-60"
                  />
                  {refundChargeForm.total_amount_paid && refundChargeForm.cancellation_charge && (
                    <p className="text-xs text-gray-400 mt-1">
                      ₹{Number(refundChargeForm.total_amount_paid).toLocaleString("en-IN")}
                      {" − ₹"}{Number(refundChargeForm.cancellation_charge || 0).toLocaleString("en-IN")} (charge)
                      {" − ₹"}{Number(refundChargeForm.convenience_fee || 0).toLocaleString("en-IN")} (conv. fee)
                      {Number(refundChargeForm.navcash_amount) > 0 && ` − ₹${Number(refundChargeForm.navcash_amount).toLocaleString("en-IN")} (navcash)`}
                      {" = ₹"}{Number(refundChargeForm.refund_amount || 0).toLocaleString("en-IN")}
                    </p>
                  )}
                </div>

                {refundChargeForm.cancellation_charge && refundChargeForm.refund_amount && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 space-y-0.5">
                    <div>📱 <b>Customer will see:</b></div>
                    <div>Total Fare: ₹{Number(refundChargeForm.total_amount_paid || 0).toLocaleString("en-IN")}</div>
                    <div>Airline Cancellation Charge: −₹{Number(refundChargeForm.cancellation_charge).toLocaleString("en-IN")}</div>
                    {Number(refundChargeForm.convenience_fee) > 0 && (
                      <div>Convenience Fee (forfeited): −₹{Number(refundChargeForm.convenience_fee).toLocaleString("en-IN")}</div>
                    )}
                    {Number(refundChargeForm.navcash_amount) > 0 && (
                      <div>NavCash Reversal (to wallet): +₹{Number(refundChargeForm.navcash_amount).toLocaleString("en-IN")}</div>
                    )}
                    {Number(refundChargeForm.giftcard_amount) > 0 && (
                      <div>Gift Card Reversal: +₹{Number(refundChargeForm.giftcard_amount).toLocaleString("en-IN")}</div>
                    )}
                    {Number(refundChargeForm.promo_code_amount) > 0 && (
                      <div>Promo Code (forfeited): −₹{Number(refundChargeForm.promo_code_amount).toLocaleString("en-IN")}</div>
                    )}
                    <div className="font-semibold border-t border-blue-200 pt-0.5 mt-0.5">
                      Cash Refund: ₹{Number(refundChargeForm.refund_amount).toLocaleString("en-IN")}
                      {Number(refundChargeForm.navcash_amount) > 0 && ` + ₹${Number(refundChargeForm.navcash_amount).toLocaleString("en-IN")} NavCash`}
                    </div>
                  </div>
                )}

                {refundChargeSubmitted ? (
                  <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 font-semibold">
                    ✅ Customer has been notified with the refund breakdown.
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={
                      refundChargeSubmitting ||
                      !refundChargeForm.cancellation_charge ||
                      !refundChargeForm.refund_amount ||
                      Number(refundChargeForm.cancellation_charge) < 0 ||
                      Number(refundChargeForm.refund_amount) < 0
                    }
                    onClick={async () => {
                      setRefundChargeSubmitting(true);
                      try {
                        await api.post(
                          `/admin/tickets/${ticket.ticket_id}/notify-refund-charges`,
                          {
                            cancellation_charge: Number(refundChargeForm.cancellation_charge),
                            refund_amount: Number(refundChargeForm.refund_amount),
                            convenience_fee: Number(refundChargeForm.convenience_fee || 0),
                            total_amount_paid: Number(refundChargeForm.total_amount_paid || 0),
                            navcash_amount: Number(refundChargeForm.navcash_amount || 0),
                            giftcard_amount: Number(refundChargeForm.giftcard_amount || 0),
                            promo_code_amount: Number(refundChargeForm.promo_code_amount || 0),
                          }
                        );
                        toast.success("Customer notified with refund breakdown.");
                        setRefundChargeSubmitted(true);
                      } catch (err) {
                        toast.error(
                          err?.response?.data?.message ||
                            err?.message ||
                            "Failed to send notification"
                        );
                      } finally {
                        setRefundChargeSubmitting(false);
                      }
                    }}
                    className="w-full rounded-xl bg-blue-600 px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                  >
                    {refundChargeSubmitting ? "Sending…" : "Send Notification to Customer"}
                  </button>
                )}

                <p className="text-xs text-gray-400">
                  The customer will receive a push notification with the full payment breakdown. They can tap &quot;Cancel Ticket&quot; in the notification to proceed with cancellation via the bot.
                </p>
              </div>
            </div>

            {/* Step 3 — Execute Cancellation & Refund via TBO (unlocks after customer_accepted) */}
            {ticket.cancellation_status === "customer_accepted" && (
              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Step 3 — Execute Cancellation &amp; Refund via TBO
                </div>
                <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 mb-3">
                  ✅ Customer confirmed. You can now execute the cancellation and process the refund through TBO.
                </div>

                {refundExecDone ? (
                    <div className="space-y-3">
                      <div className={`rounded-xl border px-4 py-3 text-sm font-semibold ${refundExecResult?.partial ? "bg-yellow-50 border-yellow-300 text-yellow-800" : "bg-green-50 border-green-200 text-green-700"}`}>
                        {refundExecResult?.partial
                          ? "⚠️ Cancellation processed but cash refund needs follow-up. See ticket history."
                          : "✅ Cancellation and refund executed successfully."}
                      </div>
                      <button
                        type="button"
                        className="w-full rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
                        onClick={async () => {
                          try {
                            const refreshRes = await api.post("/admin/tickets/detail", { ticket_id });
                            setTicket(normalizeTicketDetail(refreshRes.data?.data ?? null));
                          } catch { /* ignore */ }
                        }}
                      >
                        Refresh Ticket
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-semibold text-gray-600 block mb-1">
                          Remarks <span className="text-gray-400 font-normal text-xs">(optional)</span>
                        </label>
                        <textarea
                          value={refundExecRemarks}
                          onChange={(e) => setRefundExecRemarks(e.target.value)}
                          rows={2}
                          placeholder="Optional notes for TBO provider…"
                          disabled={refundExecSubmitting}
                          className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black resize-none disabled:opacity-60"
                        />
                      </div>
                      <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                        ⚠️ This is irreversible. TBO will cancel the booking and initiate the refund.
                      </div>
                      <button
                        type="button"
                        disabled={refundExecSubmitting}
                        onClick={async () => {
                          setRefundExecSubmitting(true);
                          try {
                            const res = await api.post(`/admin/tickets/${ticket.ticket_id}/cancel`, {
                              cancel_type: 1,
                              remarks: `[REFUND REQUEST] ${refundExecRemarks}`.trim(),
                            });
                            setRefundExecResult({ partial: !!res.data?.partial_success });
                            setRefundExecDone(true);
                            toast.success("Cancellation and refund executed via TBO.");
                            const refreshRes = await api.post("/admin/tickets/detail", { ticket_id });
                            setTicket(normalizeTicketDetail(refreshRes.data?.data ?? null));
                          } catch (err) {
                            const status = err?.response?.status;
                            const data = err?.response?.data;
                            if (status === 207 && data?.partial_success) {
                              setRefundExecResult({ partial: true });
                              setRefundExecDone(true);
                              toast.error("Cancellation done but cash refund needs follow-up.");
                            } else {
                              toast.error(data?.message || err?.message || "Execution failed");
                            }
                          } finally {
                            setRefundExecSubmitting(false);
                          }
                        }}
                        className="w-full rounded-xl bg-red-600 px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                      >
                        {refundExecSubmitting ? "Processing via TBO…" : "Execute Cancellation & Refund via TBO"}
                      </button>
                    </div>
                  )}
              </div>
            )}

          </div>
        </div>
      )}
    </>
  );
  const blkReschedulingPanel = (
    <>
      {!ticketHasNoPnr &&
        (ticket.query_type || "").toUpperCase() === "RESCHEDULING" && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            Change the flight: step by step
          </div>
          <div className="p-4">
            <ReschedulingPanel
              ticket={ticket}
              ticket_id={ticket_id}
              booking={[...(bookings?.upcoming || []), ...(bookings?.current || [])][0] ?? null}
              bookings={bookings}
              formatINR={formatINR}
              onTicketRefresh={async () => {
                const res = await api.post("/admin/tickets/detail", { ticket_id });
                setTicket(normalizeTicketDetail(res.data?.data ?? null));
              }}
            />
          </div>
        </div>
      )}
    </>
  );
  const blkCancellationPanel = (
    <>
      {!ticketHasNoPnr &&
        (ticket.query_type || "").toUpperCase() === "CANCELLATION" && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            {ticket.linked_ticket_id ? (
              <>
                Flight 1
                {(() => {
                  const b = [...(bookings?.upcoming || []), ...(bookings?.current || [])][0];
                  return b?.origin && b?.destination ? ` · ${b.origin} → ${b.destination}` : "";
                })()}
                {ticket.pnr ? ` · PNR ${ticket.pnr}` : ""}
              </>
            ) : (
              "Cancellation"
            )}
          </div>
          <div className="p-4 space-y-4">
            {isDirectionCancelTicket(ticket) ? (
            <DirectionCancelPanel
              ticket={ticket}
              ticket_id={ticket_id}
              booking={[...(bookings?.upcoming || []), ...(bookings?.current || [])][0] ?? null}
              formatINR={formatINR}
              onTicketRefresh={async () => {
                const res = await api.post("/admin/tickets/detail", { ticket_id });
                setTicket(normalizeTicketDetail(res.data?.data ?? null));
              }}
            />
            ) : (
            <CancellationPanel
              ticket={ticket}
              ticket_id={ticket_id}
              booking={[...(bookings?.upcoming || []), ...(bookings?.current || [])][0] ?? null}
              passengersWithIds={passengersWithIds}
              selectedPassengers={selectedPassengers}
              cancelType={cancelType}
              setCancelType={setCancelType}
              cancelTypeLabel={cancelTypeLabel}
              setCancelTypeLabel={setCancelTypeLabel}
              partialCancelSelectionError={partialCancelSelectionError}
              cancellationTypeSelectOptions={cancellationTypeSelectOptions}
              formatINR={formatINR}
              wholeTrip={Boolean(ticket.linked_ticket_id)}
              onTicketRefresh={async () => {
                const res = await api.post("/admin/tickets/detail", { ticket_id });
                setTicket(normalizeTicketDetail(res.data?.data ?? null));
              }}
            />
            )}
          </div>
        </div>
      )}
    </>
  );
  const blkFlight2 = (
    <>
      {/* Whole Trip: the second flight, handled in this same ticket. */}
      {!ticketHasNoPnr &&
        (ticket.query_type || "").toUpperCase() === "CANCELLATION" &&
        ticket.linked_ticket_id && (
        <WholeTripFlightSection
          key={`${ticket.linked_ticket_id}-${companionReload}`}
          ticketId={ticket.linked_ticket_id}
          label="Flight 2"
          formatINR={formatINR}
          onLoaded={handleCompanionLoaded}
          onChanged={async () => {
            const res = await api.post("/admin/tickets/detail", { ticket_id });
            setTicket(normalizeTicketDetail(res.data?.data ?? null));
          }}
        />
      )}
    </>
  );
  const blkBookingFailed = (
    <>
      {(ticket.query_type || "").toUpperCase() === "BOOKING_FAILED" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-purple-300 bg-purple-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span aria-hidden>🚨</span>
              <span className="text-sm font-bold text-purple-800">
                Booking Failed — Action Required
              </span>
            </div>
            <p className="text-xs text-purple-700">
              PNR was not generated. Customer has been charged. Resolve within 90 minutes.
            </p>
          </div>

          <div className="border rounded-2xl overflow-hidden bg-white">
            <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span aria-hidden>✈️</span>
                <span>Flight Details</span>
              </div>
              {[...(bookings?.upcoming || []), ...(bookings?.current || [])][0] && (
                <button
                  type="button"
                  onClick={openEditBookingModal}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            <div className="p-4">
              {(() => {
                const upcoming = bookings?.upcoming || [];
                const current = bookings?.current || [];
                const b = [...upcoming, ...current][0];
                if (!b) {
                  return (
                    <p className="text-sm text-gray-600">
                      {ticketHasNoPnr
                        ? "No booking details yet. Enter the confirmed PNR or TBO Booking ID below to load this section, or use instant refund below if the customer was charged without a booking record."
                        : "Loading booking details..."}
                    </p>
                  );
                }
                return (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Route</span>
                      <span className="font-semibold">
                        {b.origin} → {b.destination}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Departure</span>
                      <span className="font-semibold">
                        {b.dep_date_time
                          ? new Date(b.dep_date_time).toLocaleString("en-IN")
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Flight</span>
                      <span className="font-semibold">
                        {b.airline_code}
                        {b.airline_flight_no ? ` — ${b.airline_flight_no}` : ""}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Fare Paid</span>
                      <span className="font-bold text-blue-700">
                        ₹{Number(b.totalfare || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment ID</span>
                      <span className="font-mono text-xs">{b.payment_id || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Booking Ref</span>
                      <span className="font-semibold">{b.booking_ticket_number || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">TBO Booking ID</span>
                      <span className="font-mono text-xs">{b.flight_id || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">PNR</span>
                      <span className="font-semibold font-mono">{b.pnr || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Airline PNR</span>
                      <span className="font-semibold font-mono">{b.airline_pnr || "—"}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── Edit booking fields modal ─────────────────────────── */}
          {editBookingOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-gray-900">Edit Booking Details</h3>
                  <button
                    type="button"
                    onClick={closeEditBookingModal}
                    className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                  >
                    ×
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Update TBO Booking ID, PNR, and Airline PNR. Provide either PNR or Airline PNR (or both).
                </p>

                {!editBookingConfirmOpen ? (
                  <form onSubmit={handleEditBookingSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">
                        TBO Booking ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${editBookingErrors.flightId ? "border-red-500" : ""}`}
                        value={editBookingFlightId}
                        onChange={(e) => {
                          setEditBookingFlightId(e.target.value);
                          if (editBookingErrors.flightId) setEditBookingErrors((p) => ({ ...p, flightId: "" }));
                        }}
                        placeholder="TBO Booking ID (flight_id)"
                      />
                      {editBookingErrors.flightId && (
                        <p className="mt-1 text-xs text-red-600">{editBookingErrors.flightId}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">PNR</label>
                      <input
                        type="text"
                        className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-black ${editBookingErrors.pnr ? "border-red-500" : ""}`}
                        value={editBookingPnr}
                        onChange={(e) => {
                          setEditBookingPnr(e.target.value.toUpperCase());
                          if (editBookingErrors.pnr) setEditBookingErrors((p) => ({ ...p, pnr: "" }));
                        }}
                        placeholder="e.g. ABCXYZ (or provide Airline PNR)"
                        maxLength={10}
                      />
                      {editBookingErrors.pnr && (
                        <p className="mt-1 text-xs text-red-600">{editBookingErrors.pnr}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Airline PNR <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        className="mt-1 w-full border rounded-xl px-3 py-2 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-black"
                        value={editBookingAirlinePnr}
                        onChange={(e) => setEditBookingAirlinePnr(e.target.value.toUpperCase())}
                        placeholder="Airline PNR (optional)"
                        maxLength={20}
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={closeEditBookingModal}
                        className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                      >
                        Review &amp; Save
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">Please confirm the following values before saving.</p>
                    <div className="space-y-2 p-4 bg-gray-50 rounded-xl text-sm">
                      <div className="flex justify-between gap-4">
                        <span className="font-semibold text-gray-600">TBO Booking ID:</span>
                        <span className="text-gray-900 font-medium font-mono">{(editBookingFlightId ?? "").trim() || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="font-semibold text-gray-600">PNR:</span>
                        <span className="text-gray-900 font-medium font-mono">{(editBookingPnr ?? "").trim() || "—"}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="font-semibold text-gray-600">Airline PNR:</span>
                        <span className="text-gray-900 font-medium font-mono">{(editBookingAirlinePnr ?? "").trim() || "—"}</span>
                      </div>
                    </div>

                    {editBookingNameMismatch && (
                      <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        Passenger name mismatch with TBO. The booking details cannot be saved.
                      </div>
                    )}

                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setEditBookingConfirmOpen(false);
                          setEditBookingNameMismatch(null);
                        }}
                        disabled={editBookingSaving}
                        className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Back
                      </button>
                      {!editBookingNameMismatch && (
                        <button
                          type="button"
                          onClick={() => handleEditBookingConfirm()}
                          disabled={editBookingSaving}
                          className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                        >
                          {editBookingSaving ? "Saving…" : "Confirm & Save"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border rounded-2xl overflow-hidden bg-white">
            <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">Agent Checklist</div>
            <div className="p-4 space-y-2 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <span>1️⃣</span>
                <span>Check the airline system — is the flight still available?</span>
              </div>
              <div className="flex items-start gap-2">
                <span>2️⃣</span>
                <span>If available — verify price is unchanged vs fare paid above</span>
              </div>
              <div className="flex items-start gap-2">
                <span>3️⃣</span>
                <span>If OK — book manually in the airline system, get PNR, enter below</span>
              </div>
              <div className="flex items-start gap-2">
                <span>4️⃣</span>
                <span>If unavailable or price changed — issue instant refund below</span>
              </div>
            </div>
          </div>

          <div className="border rounded-2xl overflow-hidden bg-white">
            <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b flex items-center gap-2">
              <span aria-hidden>✅</span>
              <span>Flight Available — Enter Booking Details</span>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-gray-500">
                Enter the PNR and/or TBO Booking ID TBO support confirmed was actually
                ticketed. We verify it with TBO before recovering the booking — the
                ticket auto-closes once that verification succeeds.
              </p>

              {/* PNR */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  PNR <span className="text-gray-400 font-normal">(enter this and/or TBO Booking ID)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. ABCXYZ"
                  value={bookingFailedPnr}
                  onChange={(e) => {
                    setBookingFailedPnr(e.target.value.toUpperCase());
                    if (bookingFailedResult) setBookingFailedResult(null);
                  }}
                  className="w-full border rounded-xl px-3 py-2 text-sm font-mono font-semibold tracking-widest uppercase"
                  maxLength={10}
                />
              </div>

              {/* TBO Booking ID */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  TBO Booking ID
                </label>
                <input
                  type="text"
                  placeholder="TBO Booking ID (flight_id)"
                  value={bookingFailedTboBookingId}
                  onChange={(e) => {
                    setBookingFailedTboBookingId(e.target.value.trim());
                    if (bookingFailedResult) setBookingFailedResult(null);
                  }}
                  className="w-full border rounded-xl px-3 py-2 text-sm font-mono"
                />
              </div>

              {bookingFailedResult?.type === "lookup_failed" && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                  <div className="font-semibold mb-1">TBO could not find a match</div>
                  <div>{bookingFailedResult.message}</div>
                  {bookingFailedResult.checkedLine && (
                    <div className="mt-1 text-xs text-red-700">{bookingFailedResult.checkedLine}</div>
                  )}
                  <div className="mt-1 text-xs text-red-700">Double-check for a typo and try again.</div>
                </div>
              )}

              {bookingFailedResult?.type === "not_ticketed" && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="font-semibold mb-1">⚠️ TBO found this booking, but it isn&apos;t ticketed yet</div>
                  <div>{bookingFailedResult.message}</div>
                  <div className="mt-1 text-xs text-amber-800">
                    This booking can&apos;t be recovered yet. Consider using the refund option below instead.
                  </div>
                </div>
              )}

              {bookingFailedResult?.type === "name_mismatch" && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                  Passenger name mismatch with TBO. The booking details cannot be saved.
                </div>
              )}

              {bookingFailedResult?.type === "in_progress" && (
                <div className="rounded-xl border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
                  <div className="font-semibold mb-1">This booking is already being handled</div>
                  <div>{bookingFailedResult.message}</div>
                  <button
                    type="button"
                    onClick={async () => {
                      const res = await api.post("/admin/tickets/detail", { ticket_id: Number(ticket_id) });
                      setTicket(res.data?.data);
                      setBookingFailedResult(null);
                    }}
                    className="mt-2 text-xs font-semibold text-blue-700 underline hover:text-blue-900"
                  >
                    Refresh ticket
                  </button>
                </div>
              )}

              {bookingFailedResult?.type !== "name_mismatch" && (
                <button
                  type="button"
                  onClick={handleEnterBookingFailedPnr}
                  disabled={(!bookingFailedPnr.trim() && !bookingFailedTboBookingId.trim()) || bookingFailedPnrLoading}
                  className="w-full bg-green-600 text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50"
                >
                  {bookingFailedPnrLoading ? "Saving..." : "Save & Close Ticket"}
                </button>
              )}
            </div>
          </div>

          <div className="border rounded-2xl overflow-hidden bg-white border-red-200">
            <div className="bg-red-50 px-4 py-3 text-sm font-semibold border-b border-red-200 flex items-center gap-2">
              <span aria-hidden>💸</span>
              <span className="text-red-800">
                Flight Unavailable / Price Changed — Issue Refund
              </span>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-gray-500">
                Issue a full instant refund to the customer. Ticket will auto-close after refund is processed.
              </p>
              <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                ⚠️ This action is irreversible. Confirm with your manager before proceeding.
              </div>
              <button
                type="button"
                onClick={handleInstantRefund}
                disabled={instantRefundLoading}
                className="w-full bg-red-600 text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50"
              >
                {instantRefundLoading ? "Processing..." : "Issue Instant Full Refund"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  const blkAirlineVerification = (
    <>
      {!ticketHasNoPnr &&
        (ticket.query_type || "").toUpperCase() === "REFUND" && (
        <div className="border rounded-2xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
            Airline Verification &amp; Refund
          </div>
          <div className="p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={tboVerified}
                onChange={(e) => setTboVerified(e.target.checked)}
                className="rounded border-gray-300"
              />
              Verified in the airline system
            </label>
            {!tboVerified ? (
              <div className="p-3 rounded-xl bg-gray-100 text-sm text-gray-600 flex items-center gap-2">
                <span>🔒</span>
                Refund section locked. Please verify in the airline system first.
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">Refund Amount (₹)</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 block mb-1">Refund Reason</label>
                  <textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    rows={3}
                    className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black resize-none"
                  />
                </div>
                <button
                  type="button"
                  disabled={refundSaving}
                  onClick={async () => {
                    setRefundSaving(true);
                    try {
                      const res = await api.post(
                        `/admin/tickets/${ticket_id}/refund`,
                        {
                          amount: refundAmount,
                          reason: refundReason,
                        }
                      );
                      if (res.data?.success && !res.data?.partial_success) {
                        toast.success("Refund processed successfully");
                        setRefundAmount("");
                        setRefundReason("");
                      } else if (res.data?.partial_success) {
                        toast.error(
                          "The refund partially completed. The cash refund to the customer's payment method failed and the ticket has been flagged for follow-up."
                        );
                        const refreshed = await api.post(
                          "/admin/tickets/detail",
                          { ticket_id: Number(ticket_id) }
                        );
                        setTicket(
                          normalizeTicketDetail(refreshed.data?.data ?? null)
                        );
                      } else {
                        toast.error(
                          res.data?.message || "Refund failed"
                        );
                      }
                    } catch (err) {
                      const status = err?.response?.status;
                      const data = err?.response?.data;
                      if (status === 207 && data?.partial_success) {
                        toast.error(
                          data?.message ||
                            "The refund partially completed. The cash refund to the customer's payment method failed and the ticket has been flagged for follow-up."
                        );
                        const refreshed = await api.post(
                          "/admin/tickets/detail",
                          { ticket_id: Number(ticket_id) }
                        );
                        setTicket(
                          normalizeTicketDetail(refreshed.data?.data ?? null)
                        );
                      } else if (status === 409) {
                        toast.error(
                          data?.message ||
                            "This action is already in progress. Please refresh the page."
                        );
                      } else {
                        toast.error(
                          data?.message ||
                            err?.message ||
                            "Refund failed"
                        );
                      }
                    } finally {
                      setRefundSaving(false);
                    }
                  }}
                  className="w-full rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                >
                  {refundSaving ? "Processing..." : "Process Refund"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
  const blkSuggestedSteps = (
    <>
      <div className="border rounded-2xl overflow-hidden bg-white">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
          Suggested Next Steps
        </div>
        <div className="p-4">
          <div className="text-xs text-amber-700 mb-2">ℹ️ For internal use only — not editable</div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-gray-700 whitespace-pre-wrap break-words overflow-hidden">
            {ticket.action_required ? (
              (() => {
                const sections = formatActionRequired(ticket.action_required);
                if (sections.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 italic">
                      No actions required.
                    </p>
                  );
                }
                return (
                  <div className="space-y-4">
                    {sections.map((section, idx) => (
                      <div key={idx}>
                        {section.title && (
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            {section.title}
                          </div>
                        )}
                        <div className="space-y-1">
                          {section.items.map((item, i) => (
                            <div
                              key={i}
                              className="text-sm text-gray-700 leading-relaxed break-all"
                            >
                              {formatISOTimestampsInText(item)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : ticket.suggestion ? (
              ticket.suggestion
            ) : (
              "No suggestions available for this ticket type."
            )}
          </div>
        </div>
      </div>
    </>
  );
  const blkComments = (
    <>
      <div className="border rounded-2xl overflow-hidden bg-white">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
          Comments
        </div>
        <div className="p-4 space-y-3">
          {!ticket.comments || ticket.comments.length === 0 ? (
            <div className="text-sm text-gray-500">No comments yet.</div>
          ) : (
            <div className="space-y-2">
              {ticket.comments.map((c, idx) => (
                <div key={c.id || idx} className="bg-gray-50 rounded-xl border p-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-gray-800">{c.agent_name || c.agent || "—"}</span>
                    <span className="text-xs text-gray-400">
                      {formatHistoryTimestamp(c.created_at || c.timestamp)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700">{c.comment || c.text || "—"}</div>
                </div>
              ))}
            </div>
          )}
          <div className="pt-2 border-t border-gray-200">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add an internal comment..."
              rows={3}
              className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black resize-none"
            />
            <button
              type="button"
              disabled={commentSaving || !(commentText || "").trim()}
              onClick={async () => {
                const text = (commentText || "").trim();
                if (!text) return;
                setCommentSaving(true);
                try {
                  await api.post(`/admin/tickets/${ticket_id}/comments`, { comment: text });
                  toast.success("Comment added");
                  setCommentText("");
                  const res = await api.post("/admin/tickets/detail", { ticket_id });
                  const refreshed = res.data?.data ?? ticket;
                  if (refreshed) {
                    refreshed.history =
                      refreshed.TicketHistories ||
                      refreshed.ticket_histories ||
                      refreshed.history ||
                      [];
                    refreshed.comments =
                      refreshed.TicketComments ||
                      refreshed.ticket_comments ||
                      refreshed.comments ||
                      [];
                  }
                  setTicket(refreshed);
                } catch (err) {
                  toast.error(err?.response?.data?.message || err?.message || "Failed to post comment");
                } finally {
                  setCommentSaving(false);
                }
              }}
              className="mt-2 w-full rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
            >
              {commentSaving ? "Posting..." : "Post Comment"}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="glossary">
      <div className="flex items-center justify-between gap-3 mb-6">
        {isCancellationLayout ? (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold">Ticket #{ticket.ticket_id ?? ticket_id}</h1>
              <StatusChip ticket={ticket} />
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {(() => {
                const req = ticket.customer_request || {};
                const what = req.scope === "WHOLE_TRIP" || ticket.linked_ticket_id ? "Cancel whole trip" : "Cancellation";
                const routes = Array.isArray(req.routes) && req.routes.length ? req.routes.join(" and ") : null;
                const pax = req.passenger_count ? `${req.passenger_count} passenger${req.passenger_count === 1 ? "" : "s"}` : null;
                const who = ticket.customer_name || null;
                return [what, routes, pax, who, ticket.pnr ? `PNR ${ticket.pnr}` : null].filter(Boolean).join(" · ");
              })()}
            </p>
          </div>
        ) : isRescheduleLayout ? (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold">Ticket #{ticket.ticket_id ?? ticket_id}</h1>
              <RescheduleStatusChip ticket={ticket} />
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {(() => {
                const d = ticket.reschedule_details_parsed || {};
                const route = d.new_origin && d.new_destination ? `${d.new_origin} → ${d.new_destination}` : null;
                let when = null;
                if (d.new_departure) {
                  try {
                    when = new Date(d.new_departure).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });
                  } catch { when = null; }
                }
                return ["Change flight date", route, when ? `new flight ${when}` : null, ticket.customer_name || null, ticket.pnr ? `PNR ${ticket.pnr}` : null]
                  .filter(Boolean)
                  .join(" · ");
              })()}
            </p>
          </div>
        ) : (
          <h1 className="text-2xl font-bold">Ticket #{ticket.ticket_id ?? ticket_id}</h1>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin/tickets")}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            Back to List
          </button>
          <button
            type="button"
            onClick={() => setGlossaryOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          >
            <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
            Glossary
          </button>
        </div>
      </div>

      {Number(ticket.needs_refund_recovery) === 1 ? (
        <RecoveryBanner
          reason={ticket.refund_recovery_reason}
          flaggedAt={ticket.refund_recovery_set_at}
          onRetry={handleRetryFailedRefund}
          retryLoading={retryLoading}
          onRefresh={async () => {
            setRecoveryRefreshLoading(true);
            try {
              await refreshTicketDetail();
              toast.success("Ticket data refreshed.");
            } catch (err) {
              toast.error(
                err?.response?.data?.message ||
                  err?.message ||
                  "Could not refresh ticket data."
              );
            } finally {
              setRecoveryRefreshLoading(false);
            }
          }}
          refreshLoading={recoveryRefreshLoading}
        />
      ) : null}

      {duplicateTickets.length > 0 && ticket?.pnr && (
        <div className="rounded-2xl border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3">
          <span className="text-yellow-600 text-xl mt-0.5" aria-hidden>
            ⚠️
          </span>
          <div>
            <div className="text-sm font-semibold text-yellow-800">
              {duplicateTickets.length} other open ticket
              {duplicateTickets.length > 1 ? "s" : ""} found
              for PNR {ticket.pnr}
            </div>
            <div className="flex flex-wrap gap-2 mt-1">
              {duplicateTickets.map((t) => (
                <a
                  key={t.ticket_id}
                  href={`/admin/tickets/${t.ticket_id}`}
                  className="text-xs text-yellow-700 underline hover:text-yellow-900"
                >
                  #{t.ticket_id} ({t.query_type})
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {(ticket.query_type || "").toUpperCase() === "BOOKING_FAILED" && (
        <div className="mb-6 rounded-2xl border-2 border-purple-300 bg-purple-50 p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl" aria-hidden>🚨</span>
            <div className="flex-1">
              <div className="text-sm font-bold text-purple-900 mb-1">
                Booking Failed — PNR Not Generated
              </div>
              <p className="text-xs text-purple-800 leading-relaxed">
                Customer was charged but the booking did not complete at the airline.
                Resolve within 90 minutes — either book manually and enter the new PNR,
                or issue an instant full refund. See the Booking Failed workflow on
                the right for actions.
              </p>
              {ticket.customer_history?.is_red_flag && (
                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">
                  🚩 Red Flag customer — review history before processing refund
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isCancellationLayout ? (
        // Cancellation tickets: the work (what to do next, each flight's steps,
        // activity) in the wide column; reference data in a side column, with
        // the long sections folded away.
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6">
          <div className="space-y-6 min-w-0">
            <NextActionCard ticket={ticket} companion={companionTicket} />
            {blkCancellationPanel}
            {blkFlight2}
            {ticket.linked_ticket_id ? (
              <WholeTripSendCard
                primaryTicketId={ticket_id}
                formatINR={formatINR}
                flights={[
                  (() => {
                    const b = [...(bookings?.upcoming || []), ...(bookings?.current || [])][0] ?? null;
                    return { label: "Flight 1", ticket, booking: b, route: b?.origin && b?.destination ? `${b.origin} → ${b.destination}` : null };
                  })(),
                  {
                    label: "Flight 2",
                    ticket: companionTicket,
                    booking: companionBooking,
                    route: companionBooking?.origin && companionBooking?.destination ? `${companionBooking.origin} → ${companionBooking.destination}` : null,
                  },
                ]}
                onSent={async () => {
                  const res = await api.post("/admin/tickets/detail", { ticket_id });
                  setTicket(normalizeTicketDetail(res.data?.data ?? null));
                  setCompanionReload((n) => n + 1);
                }}
              />
            ) : null}
            {blkHistory}
            {blkComments}
          </div>
          <aside className="space-y-6 min-w-0">
            {blkStatus}
            {blkTicketCustomer}
            {blkCustomerHistory}
            <FoldSection title="Booking & payment">{blkBookings}</FoldSection>
            <FoldSection title="Passengers">{blkPassengers}</FoldSection>
            <FoldSection title="Full booking record (advanced)">{blkBookingDetail}</FoldSection>
            {cancellationRequestDetails ? (
              <FoldSection title="Request text from the ticket">{blkCancellationRequest}</FoldSection>
            ) : null}
            <FoldSection title="System notes">{blkSuggestedSteps}</FoldSection>
          </aside>
        </div>
      ) : isRescheduleLayout ? (
        // Reschedule tickets: same work-first layout as cancellations.
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6">
          <div className="space-y-6 min-w-0">
            <RescheduleNextActionCard ticket={ticket} />
            {blkReschedulingPanel}
            {blkHistory}
            {blkComments}
          </div>
          <aside className="space-y-6 min-w-0">
            {blkStatus}
            {blkTicketCustomer}
            {blkCustomerHistory}
            <FoldSection title="Booking & payment">{blkBookings}</FoldSection>
            <FoldSection title="Passengers">{blkPassengers}</FoldSection>
            <FoldSection title="Full booking record (advanced)">{blkBookingDetail}</FoldSection>
            {reschedulingRequestDetails ? (
              <FoldSection title="What the customer wrote">{blkReschedulingRequest}</FoldSection>
            ) : null}
            <FoldSection title="System notes">{blkSuggestedSteps}</FoldSection>
          </aside>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-6">
          <div className="space-y-6">
            {blkTicketCustomer}
            {blkBookings}
            {blkPassengers}
            {blkBookingDetail}
            {blkHistory}
          </div>

          <div className="space-y-6">
            {blkBookingFailedCustomer}
            {blkStatus}
            {blkQueryType}
            {blkDgca}
            {blkCustomerHistory}
            {blkCancellationStatus}
            {blkCancellationRequest}
            {blkReschedulingRequest}
            {blkRefundRequest}
            {blkRefundProcessing}
            {blkReschedulingPanel}
            {blkCancellationPanel}
            {blkFlight2}
            {blkBookingFailed}
            {blkAirlineVerification}
            {blkSuggestedSteps}
            {blkComments}
          </div>
        </div>
      )}

      {showClosingModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="close-ticket-title"
        >
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
            <h2 id="close-ticket-title" className="text-lg font-bold text-gray-900">
              Close Ticket
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please add a closing comment before closing this ticket. This will be saved and visible
              to all agents.
            </p>
            <textarea
              required
              rows={4}
              placeholder="Describe the resolution or reason for closing..."
              value={closingComment}
              onChange={(e) => setClosingComment(e.target.value)}
              disabled={statusSaving}
              className="mt-4 w-full resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
            />
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={statusSaving}
                onClick={() => {
                  setShowClosingModal(false);
                  setClosingComment("");
                  setPendingCloseStatus("");
                }}
                className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={statusSaving || !(closingComment || "").trim()}
                onClick={async () => {
                  const comment = (closingComment || "").trim();
                  if (!comment) return;
                  setStatusSaving(true);
                  try {
                    const res = await api.post("/admin/tickets/update-status", {
                      ticket_id,
                      status: pendingCloseStatus || "Closed",
                      closing_comment: comment,
                    });
                    if (!res.data?.success) {
                      throw new Error(res.data?.message || "Update failed");
                    }
                    toast.success("Ticket closed successfully");
                    setTicket((t) => ({ ...t, status: "Closed" }));
                    setStatusValue("Closed");
                    setShowClosingModal(false);
                    setClosingComment("");
                    setPendingCloseStatus("");
                    const refreshRes = await api.post("/admin/tickets/detail", {
                      ticket_id,
                    });
                    const ticketData = refreshRes.data?.data ?? null;
                    if (ticketData) {
                      ticketData.history =
                        ticketData.TicketHistories ||
                        ticketData.ticket_histories ||
                        ticketData.history ||
                        [];
                      ticketData.comments =
                        ticketData.TicketComments ||
                        ticketData.ticket_comments ||
                        ticketData.comments ||
                        [];
                    }
                    setTicket(ticketData);
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.message || err?.message || "Update failed"
                    );
                  } finally {
                    setStatusSaving(false);
                  }
                }}
                className="rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
              >
                Close Ticket
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <AgentGlossaryModal open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
      <RawBookingPayloadModal
        open={rawPayloadModal.open}
        onClose={() => setRawPayloadModal({ open: false, type: null })}
        type={rawPayloadModal.type}
        booking={linkedBookingDetail}
      />
    </div>
  );
}
